import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Plus, Search, X, Edit, Trash2, Save, DollarSign, TrendingUp, Percent, Tag, Settings, Upload, FileSpreadsheet, List, Grid, Image, Video, Link, Clipboard, ChevronUp, ChevronDown, ChevronLeft, Download, AlertTriangle, Check, CheckCircle, SlidersHorizontal, GripVertical, Eye, EyeOff, ArrowUpDown, ArrowUp, ArrowDown, Copy } from 'lucide-react';
import * as XLSX from 'xlsx';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { catalogService } from '../services/catalogService';
import { api } from '../config/api';
import { clientService } from '../services/clientService';
import CatalogsManager from './CatalogsManager';
import ProductIndustrialSection from './ProductIndustrialSection';
import { parseErrorMessage } from '../utils/helpers';

const ALL_COLUMNS = [
  { id: 'name',               label: 'Producto',     defaultVisible: true,  sortKey: 'name' },
  { id: 'photo',              label: 'Foto',         defaultVisible: true },
  { id: 'product_code',       label: 'Código',       defaultVisible: false, sortKey: 'product_code' },
  { id: 'category',           label: 'Categoría',    defaultVisible: false, sortKey: 'category' },
  { id: 'brand',              label: 'Marca',        defaultVisible: false },
  { id: 'product_line',       label: 'Línea',        defaultVisible: false },
  { id: 'unit_price',         label: 'Precio',       defaultVisible: false, sortKey: 'unit_price',   align: 'right' },
  { id: 'cost',               label: 'Costo',        defaultVisible: true,  sortKey: 'cost',         align: 'right' },
  { id: 'list_price',         label: 'Precio Lista', defaultVisible: true,                            align: 'right' },
  { id: 'profit_margin',      label: 'Margen',       defaultVisible: false, sortKey: 'profit_margin',align: 'right' },
  { id: 'cost_percentage',    label: 'Costo %',      defaultVisible: false, sortKey: 'cost_percentage', align: 'right' },
  { id: 'unit',               label: 'Unidad',       defaultVisible: false, sortKey: 'unit' },
  { id: 'stock',              label: 'Stock',        defaultVisible: false, sortKey: 'stock',        align: 'right' },
  { id: 'is_service',         label: 'Tipo',         defaultVisible: true },
  { id: 'is_active',          label: 'Estado',       defaultVisible: true },
  { id: 'product_status',     label: 'Fase',         defaultVisible: true },
  { id: 'brand_type',         label: 'Tipo Marca',   defaultVisible: false },
  { id: 'incoterm',           label: 'Incoterm',     defaultVisible: false },
  { id: 'moq',                label: 'MOQ',          defaultVisible: false, sortKey: 'moq',          align: 'right' },
  { id: 'moq_oem',            label: 'MOQ OEM',      defaultVisible: false, sortKey: 'moq_oem',      align: 'right' },
  { id: 'pcs_per_ctn',        label: 'PCS/CTN',      defaultVisible: false, sortKey: 'pcs_per_ctn',  align: 'right' },
  { id: 'cbm_per_ctn',        label: 'CBM/CTN',      defaultVisible: false, sortKey: 'cbm_per_ctn',  align: 'right' },
  { id: 'cost_rmb',           label: 'Costo RMB',    defaultVisible: false, sortKey: 'cost_rmb',     align: 'right' },
  { id: 'cost_fob',           label: 'Costo FOB',    defaultVisible: false, sortKey: 'cost_fob',     align: 'right' },
  { id: 'price_fob',          label: 'Precio FOB',   defaultVisible: false, sortKey: 'price_fob',    align: 'right' },
  { id: 'discount_fob',       label: 'Dto FOB %',    defaultVisible: false, sortKey: 'discount_fob', align: 'right' },
  { id: 'manufacturer_code',  label: 'Cód. Fab.',    defaultVisible: false },
  { id: 'size',               label: 'Tamaño',       defaultVisible: false },
  { id: 'pieces',             label: 'Piezas',       defaultVisible: false, sortKey: 'pieces',       align: 'right' },
  { id: 'inner_ctn',          label: 'Inner CTN',    defaultVisible: false, sortKey: 'inner_ctn',    align: 'right' },
  { id: 'assortment',         label: 'Surtido',      defaultVisible: false },
  { id: 'material_composition',label:'Material',     defaultVisible: false },
  { id: 'recommended_age',    label: 'Edad Recom.',  defaultVisible: false },
  { id: 'ean',                label: 'EAN',          defaultVisible: false },
  { id: 'dun',                label: 'DUN',          defaultVisible: false },
  { id: 'product_variant',    label: 'Variante',     defaultVisible: false },
  { id: 'added_date',         label: 'Fecha Alta',   defaultVisible: false, sortKey: 'added_date' },
  { id: 'is_for_sale',        label: 'Para Venta',   defaultVisible: false },
  { id: 'is_purchasable',     label: 'Para Compra',  defaultVisible: false },
  { id: 'actions',            label: 'Acciones',     defaultVisible: true },
];

const ProductManager = ({ 
  products = [], 
  onAddProduct = () => {},
  onUpdateProduct = () => {},
  onDeleteProduct = () => {}
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('active');
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [packagingTypes, setPackagingTypes] = useState([]);
  const [batteryTypes, setBatteryTypes] = useState([]);
  const [factories, setFactories] = useState([]);
  const [factoryContacts, setFactoryContacts] = useState([]);
  const [ports, setPorts] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [priceListsReady, setPriceListsReady] = useState(false);
  const [selectedPriceListId, setSelectedPriceListId] = useState(null);
  const [listPriceMap, setListPriceMap] = useState({}); // { productId: { price, cost_fob } }
  const [listPriceOverrides, setListPriceOverrides] = useState({}); // { productId: { listId: price } }
  const [activeSection, setActiveSection] = useState('general');
  const [publicUrl, setPublicUrl] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [showCatalogManager, setShowCatalogManager] = useState(false);
  const [catalogManagerTab, setCatalogManagerTab] = useState('brands');
  const [imagePreview, setImagePreview] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', description: '', parent: null, is_active: true });
  const [showImportModal, setShowImportModal] = useState(false);
  const [showProductImport, setShowProductImport] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'card' o 'list'

  // Column management
  const [columnConfig, setColumnConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('product_table_columns_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge in any new columns not present in the saved config
        const savedIds = new Set(parsed.map(c => c.id));
        const newCols = ALL_COLUMNS.filter(c => !savedIds.has(c.id)).map(c => ({ id: c.id, visible: c.defaultVisible }));
        return [...parsed, ...newCols];
      }
    } catch {}
    return ALL_COLUMNS.map(c => ({ id: c.id, visible: c.defaultVisible }));
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const [inlineEdit, setInlineEdit] = useState(null); // { productId, field, value }
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [savingInline, setSavingInline] = useState(false);
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [focusedColId, setFocusedColId] = useState(null); // celda "activa" dentro de la fila enfocada (estilo Excel)
  const columnPanelRef = useRef(null);
  const columnBtnRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const tableBodyRef = useRef(null);

  const getProductImage = (product) => {
    if (product?.main_image) return product.main_image;
    if (Array.isArray(product?.gallery_images) && product.gallery_images[0]) {
      return product.gallery_images[0];
    }
    return '';
  };
  
  const [formData, setFormData] = useState({
    name: '',
    product_code: '',
    description: '',
    category_id: '',
    category: '',
    brand_id: '',
    product_line_id: '',
    tag_ids: [],
    brand_type: 'GENERIC',
    product_status: 'DRAFT',
    is_for_sale: true,
    is_purchasable: true,
    available_markets: '',
    logistics_factor: '',
    unit_price: 0,
    price_currency: 'USD',
    cost: 0,
    cost_currency: 'USD',
    profit_margin: 0,
    cost_percentage: 0,
    unit: 'unidad',
    stock: 0,
    incoterm: 'FOB',
    cost_rmb: '',
    cost_fob: '',
    exchange_rate: '',
    price_fob: '',
    pcs_per_ctn: '',
    cbm_per_ctn: '',
    factory_port: '',
    shipping_port: '',
    is_service: false,
    is_active: 'true',
    assortment: '',
    pieces: '',
    material_composition: '',
    size: '',
    packaging_type_id: '',
    uses_batteries: false,
    batteries_included: false,
    recommended_age: '',
    battery_type_id: '',
    moq: '',
    moq_oem: '',
    moq_factory_pack: '',
    discount_fob: '',
    product_variant: '',
    battery_info: '',
    accessories: '',
    added_date: new Date().toISOString().slice(0, 10),
    inner_ctn: '',
    factory_id: '',
    agent_id: '',
    manufacturer_code: '',
    main_image: '',
    gallery_images: [],
    video_url: '',
    product_improvements: '',
    ecommerce_description: '',
    ce_logo: false,
    ukca_logo: false,
    cert_en71: null,
    cert_ftalatos: null,
    cert_tolueno: null,
    cert_benceno: null,
    ean: '',
    dun: '',
    product_height: '',
    product_length: '',
    product_width: '',
    master_pack_height: '',
    master_pack_length: '',
    master_pack_width: '',
    master_pack_gross_weight: '',
    master_pack_net_weight: '',
    units_per_master: '',
    original_factory_cost: '',
    best_cost_with_order: '',
    listPrices: {}
  });

  // Persistir configuración de columnas
  useEffect(() => {
    try { localStorage.setItem('product_table_columns_v2', JSON.stringify(columnConfig)); } catch {}
  }, [columnConfig]);

  const priceListColumns = useMemo(() =>
    (priceLists || []).map((pl) => ({
      id: `pl_${pl.id}`,
      label: pl.name,
      defaultVisible: true,
      align: 'right',
      isPriceList: true,
      priceListId: pl.id,
    })),
  [priceLists]);

  const allColumnDefs = useMemo(
    () => [...ALL_COLUMNS, ...priceListColumns],
    [priceListColumns]
  );

  useEffect(() => {
    if (!priceListsReady) return;
    setColumnConfig((prev) => {
      const validPlIds = new Set(priceListColumns.map((c) => c.id));
      const withoutStale = prev.filter((c) => !String(c.id).startsWith('pl_') || validPlIds.has(c.id));
      const existing = new Set(withoutStale.map((c) => c.id));
      const additions = priceListColumns
        .filter((c) => !existing.has(c.id))
        .map((c) => ({ id: c.id, visible: true }));
      if (!additions.length && withoutStale.length === prev.length) return prev;
      const idx = withoutStale.findIndex((c) => c.id === 'list_price');
      if (idx >= 0) {
        const next = [...withoutStale];
        next.splice(idx + 1, 0, ...additions);
        return next;
      }
      return [...withoutStale, ...additions];
    });
  }, [priceListColumns, priceListsReady]);

  const getProductListPrice = useCallback((product, listId) => {
    const oid = String(product.id);
    const lid = String(listId);
    if (listPriceOverrides[oid]?.[lid] != null && listPriceOverrides[oid][lid] !== '') {
      return listPriceOverrides[oid][lid];
    }
    const found = (product.list_prices || []).find((lp) => String(lp.price_list_id) === lid);
    if (found != null && found.price != null) return found.price;
    return '';
  }, [listPriceOverrides]);

  // Cerrar panel de columnas al hacer click fuera
  const floatingPanelRef = useRef(null);
  useEffect(() => {
    if (!showColumnPanel) return;
    const handler = (e) => {
      const insideBtn   = columnPanelRef.current?.contains(e.target);
      const insidePanel = floatingPanelRef.current?.contains(e.target);
      if (!insideBtn && !insidePanel) setShowColumnPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnPanel]);

  // Cargar categorías y catálogos auxiliares al montar
  useEffect(() => {
    loadCategories();
    loadCatalogs();
    loadPorts();
    loadFactoryContacts();
  }, []);

  useEffect(() => {
    if (showForm) {
      loadCatalogs();
      loadFactoryContacts();
    }
  }, [showForm]);

  useEffect(() => {
    // Si se cierra el gestor de catalogos mientras el form sigue abierto,
    // refrescar listas para mostrar altas recientes (ej: nuevas fabricas).
    if (!showCatalogManager && showForm) {
      loadCatalogs();
    }
  }, [showCatalogManager, showForm]);

  // ── Carga precios de la lista seleccionada ────────────────────────────────
  useEffect(() => {
    if (!selectedPriceListId) { setListPriceMap({}); return; }
    catalogService.priceLists.getItems(selectedPriceListId)
      .then(items => {
        const map = {};
        (Array.isArray(items) ? items : []).forEach(item => {
          map[item.product] = {
            price: parseFloat(item.price_fob_unit) || 0,
            cost_fob: parseFloat(item.cost_fob) || 0,
          };
        });
        setListPriceMap(map);
      })
      .catch(() => setListPriceMap({}));
  }, [selectedPriceListId]);

  // ── Auto-cálculo Costo FOB cuando Incoterm = EXW ──────────────────────────
  useEffect(() => {
    if ((formData.incoterm || 'FOB') !== 'EXW') return;
    const cbm     = parseFloat(formData.cbm_per_ctn);
    const pcs     = parseFloat(formData.pcs_per_ctn);
    const factor  = parseFloat(formData.logistics_factor);
    const rmb     = parseFloat(formData.cost_rmb) || 0;
    const rate    = parseFloat(formData.exchange_rate);
    if (cbm > 0 && pcs > 0 && factor > 0 && rate > 0) {
      const fob = ((cbm / pcs) * factor + rmb) / rate;
      setFormData(prev => ({ ...prev, cost_fob: fob.toFixed(6) }));
    }
  }, [formData.incoterm, formData.cbm_per_ctn, formData.pcs_per_ctn, formData.logistics_factor, formData.cost_rmb, formData.exchange_rate]);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getFlat({ active_only: false });
      setCategories(data || []);
    } catch (error) {
      console.error('Error cargando categorías:', error);
      setCategories([]);
    }
  };

  const loadCatalogs = async () => {
    try {
      const [
        brandsData,
        productLinesData,
        productTagsData,
        packagingTypesData,
        batteryTypesData,
        factoriesData,
        priceListsData,
      ] = await Promise.all([
        catalogService.brands.getAll({ active_only: false }),
        catalogService.productLines.getAll({ active_only: false }),
        catalogService.productTags.getAll({ active_only: false }),
        catalogService.packagingTypes.getAll({ active_only: false }),
        catalogService.batteryTypes.getAll({ active_only: false }),
        catalogService.factories.getAll({ active_only: false }),
        catalogService.priceLists.getAll({ active_only: true }),
      ]);
      setBrands(brandsData || []);
      setProductLines(productLinesData || []);
      setProductTags(productTagsData || []);
      setPackagingTypes(packagingTypesData || []);
      setBatteryTypes(batteryTypesData || []);
      setFactories(factoriesData || []);
      setPriceLists(priceListsData || []);
    } catch (error) {
      console.error('Error cargando catálogos de producto:', error);
      setBrands([]);
      setProductLines([]);
      setProductTags([]);
      setPackagingTypes([]);
      setBatteryTypes([]);
      setFactories([]);
    } finally {
      setPriceListsReady(true);
    }
  };

  const loadPorts = async () => {
    try {
      const portsData = await api.get('/pricing/ports/');
      setPorts(Array.isArray(portsData) ? portsData : []);
    } catch (error) {
      setPorts([]);
    }
  };

  const loadFactoryContacts = async () => {
    try {
      const data = await clientService.getAll({ contact_type: 'SUPPLIER' });
      setFactoryContacts(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      console.error('Error cargando contactos agente:', error);
      setFactoryContacts([]);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      product_code: '',
      description: '',
      category_id: '',
      category: '',
      brand_id: '',
      product_line_id: '',
      tag_ids: [],
      brand_type: 'GENERIC',
      product_status: 'DRAFT',
      is_for_sale: true,
      is_purchasable: true,
      available_markets: '',
      logistics_factor: '',
      unit_price: 0,
      price_currency: 'USD',
      cost: 0,
      cost_currency: 'USD',
      profit_margin: 0,
      cost_percentage: 0,
      unit: 'unidad',
      stock: 0,
      incoterm: 'FOB',
      cost_rmb: '',
      cost_fob: '',
      exchange_rate: '',
      price_fob: '',
      pcs_per_ctn: '',
      cbm_per_ctn: '',
      factory_port: '',
      shipping_port: '',
      is_service: false,
      is_active: 'true',
      assortment: '',
      pieces: '',
      material_composition: '',
      size: '',
      packaging_type_id: '',
      uses_batteries: false,
      batteries_included: false,
      recommended_age: '',
      battery_type_id: '',
      moq: '',
      moq_oem: '',
      moq_factory_pack: '',
      discount_fob: '',
      product_variant: '',
      battery_info: '',
      accessories: '',
      added_date: new Date().toISOString().slice(0, 10),
      inner_ctn: '',
      factory_id: '',
      agent_id: '',
      manufacturer_code: '',
      main_image: '',
      gallery_images: [],
      video_url: '',
      product_improvements: '',
      ecommerce_description: '',
      ce_logo: false,
      ukca_logo: false,
      cert_en71: null,
      cert_ftalatos: null,
      cert_tolueno: null,
      cert_benceno: null,
      ean: '',
      dun: '',
      product_height: '',
      product_length: '',
      product_width: '',
      master_pack_height: '',
      master_pack_length: '',
      master_pack_width: '',
      master_pack_gross_weight: '',
      master_pack_net_weight: '',
      units_per_master: '',
      original_factory_cost: '',
      best_cost_with_order: '',
      listPrices: {}
    });
    setEditingProduct(null);
    setActiveSection('general');
    setPublicUrl('');
    setTagSearch('');
  };

  const resetCategoryForm = () => {
    setCategoryFormData({ name: '', description: '', parent: null, is_active: true });
    setEditingCategory(null);
  };

  // Calcular margen de ganancia y costo % cuando cambian precio o costo
  const calculateMargins = (price, cost) => {
    if (price === 0) {
      return { profit_margin: 0, cost_percentage: 0 };
    }
    const profitMargin = ((price - cost) / price) * 100;
    const costPercentage = (cost / price) * 100;
    return {
      profit_margin: Math.round(profitMargin * 100) / 100,
      cost_percentage: Math.round(costPercentage * 100) / 100
    };
  };

  const handlePriceChange = (value) => {
    const price = parseFloat(value) || 0;
    const cost = formData.cost || 0;
    const margins = calculateMargins(price, cost);
    setFormData(prev => ({
      ...prev,
      unit_price: price,
      ...margins
    }));
  };

  const handleCostChange = (value) => {
    const cost = parseFloat(value) || 0;
    const price = formData.unit_price || 0;
    const margins = calculateMargins(price, cost);
    setFormData(prev => ({
      ...prev,
      cost: cost,
      ...margins
    }));
  };

  const handleMarginChange = (value) => {
    const margin = parseFloat(value) || 0;
    const price = formData.unit_price || 0;
    if (price > 0) {
      const cost = price * (1 - margin / 100);
      const costPercentage = (cost / price) * 100;
      setFormData(prev => ({
        ...prev,
        profit_margin: margin,
        cost: Math.round(cost * 100) / 100,
        cost_percentage: Math.round(costPercentage * 100) / 100
      }));
    }
  };

  const handleCostPercentageChange = (value) => {
    const costPct = parseFloat(value) || 0;
    const price = formData.unit_price || 0;
    if (price > 0) {
      const cost = price * (costPct / 100);
      const profitMargin = ((price - cost) / price) * 100;
      setFormData(prev => ({
        ...prev,
        cost_percentage: costPct,
        cost: Math.round(cost * 100) / 100,
        profit_margin: Math.round(profitMargin * 100) / 100
      }));
    }
  };

  const openCatalogManager = (tabKey) => {
    setCatalogManagerTab(tabKey);
    setShowCatalogManager(true);
  };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleCertFileUpload = async (certKey, file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      alert('Solo se permiten archivos PDF o imagen (JPEG/PNG).');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const today = new Date().toISOString().slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [certKey]: {
          file_data: dataUrl,
          file_name: file.name,
          upload_date: today,
          expiry_date: prev[certKey]?.expiry_date || '',
        }
      }));
    } catch {
      alert('No se pudo cargar el archivo.');
    }
  };

  const handleMainImageChange = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFormData(prev => ({ ...prev, main_image: dataUrl }));
    } catch (error) {
      console.error('Error leyendo imagen principal:', error);
      alert('No se pudo cargar la imagen. Verificá que el archivo no esté dañado.');
    }
  };

  const handleGalleryChange = async (files) => {
    if (!files || files.length === 0) return;
    try {
      const current = formData.gallery_images || [];
      const remaining = 3 - current.length;
      if (remaining <= 0) {
        alert('Ya hay 3 imágenes de galería (máx. 4 en total con la principal). Eliminá una para agregar otra.');
        return;
      }
      const fileArray = Array.from(files).slice(0, remaining);
      const dataUrls = await Promise.all(fileArray.map(fileToDataUrl));
      setFormData(prev => ({
        ...prev,
        gallery_images: [...(prev.gallery_images || []), ...dataUrls].slice(0, 3),
      }));
    } catch (error) {
      console.error('Error leyendo galeria:', error);
      alert('No se pudieron cargar las imágenes de galería. Verificá que los archivos no estén dañados.');
    }
  };

  const handleGallerySlotChange = async (file, slotIndex) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFormData(prev => {
        const gallery = [...(prev.gallery_images || [])];
        while (gallery.length <= slotIndex) gallery.push(null);
        gallery[slotIndex] = dataUrl;
        return { ...prev, gallery_images: gallery.filter(Boolean).slice(0, 3) };
      });
    } catch (error) {
      console.error('Error leyendo imagen de galería:', error);
      alert('No se pudo cargar la imagen. Verificá que el archivo no esté dañado.');
    }
  };

  const handleGallerySlotRemove = (slotIndex) => {
    setFormData(prev => {
      const gallery = [...(prev.gallery_images || [])];
      gallery.splice(slotIndex, 1);
      return { ...prev, gallery_images: gallery };
    });
  };

  const moveGalleryItem = (index, direction) => {
    setFormData(prev => {
      const next = [...(prev.gallery_images || [])];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return prev;
      const temp = next[index];
      next[index] = next[targetIndex];
      next[targetIndex] = temp;
      return { ...prev, gallery_images: next };
    });
  };

  const handleVideoUpload = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setFormData(prev => ({ ...prev, video_url: dataUrl }));
    } catch (error) {
      console.error('Error leyendo video:', error);
      alert('No se pudo cargar el video. Verificá que el archivo no esté dañado.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.name.trim()) {
      alert('El nombre del producto es obligatorio.');
      return;
    }

    // ── Validación campos obligatorios logísticos ──
    const logisticsErrors = [];
    if (!formData.factory_port)   logisticsErrors.push('Puerto de fábrica');
    if (!formData.shipping_port)  logisticsErrors.push('Puerto de embarque');
    if (!formData.pcs_per_ctn)    logisticsErrors.push('Piezas por caja (PCS/CTN)');
    if (!formData.cbm_per_ctn || parseFloat(formData.cbm_per_ctn) <= 0)
      logisticsErrors.push('CBM/CTN (completá las dimensiones del master pack)');
    if (logisticsErrors.length > 0) {
      alert(`Los siguientes campos son obligatorios:\n• ${logisticsErrors.join('\n• ')}`);
      return;
    }

    try {
      const productData = {
        name: formData.name.trim(),
        product_code: formData.product_code?.trim() || null,
        description: formData.description?.trim() || null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        category: formData.category?.trim() || null, // Mantener para compatibilidad
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        product_line_id: formData.product_line_id ? parseInt(formData.product_line_id) : null,
        tag_ids: Array.isArray(formData.tag_ids) ? formData.tag_ids.map(id => parseInt(id)) : [],
        brand_type: formData.brand_type || 'GENERIC',
        product_status: formData.product_status || 'DRAFT',
        is_for_sale: !!formData.is_for_sale,
        is_purchasable: !!formData.is_purchasable,
        unit_price: parseFloat(formData.unit_price) || 0,
        price_currency: formData.price_currency || 'USD',
        cost: parseFloat(formData.cost) || 0,
        cost_currency: formData.cost_currency || 'USD',
        profit_margin: parseFloat(formData.profit_margin) || 0,
        cost_percentage: parseFloat(formData.cost_percentage) || 0,
        unit: formData.unit || 'unidad',
        stock: formData.is_service ? 0 : (parseFloat(formData.stock) || 0), // Servicios no tienen stock
        incoterm: formData.incoterm || 'FOB',
        cost_rmb: formData.cost_rmb !== '' ? parseFloat(formData.cost_rmb) : null,
        cost_fob: formData.cost_fob !== '' ? parseFloat(formData.cost_fob) : null,
        exchange_rate: formData.exchange_rate !== '' ? parseFloat(formData.exchange_rate) : null,
        price_fob: formData.price_fob !== '' ? parseFloat(formData.price_fob) : null,
        pcs_per_ctn: formData.pcs_per_ctn !== '' ? parseInt(formData.pcs_per_ctn, 10) : null,
        cbm_per_ctn: formData.cbm_per_ctn !== '' ? parseFloat(formData.cbm_per_ctn) : null,
        factory_port: formData.factory_port ? parseInt(formData.factory_port, 10) : null,
        shipping_port: formData.shipping_port ? parseInt(formData.shipping_port, 10) : null,
        is_service: formData.is_service || false,
        is_active: formData.is_active || 'true',
        assortment: formData.assortment?.trim() || null,
        pieces: formData.pieces !== '' ? parseInt(formData.pieces, 10) : null,
        material_composition: formData.material_composition?.trim() || null,
        size: formData.size?.trim() || null,
        packaging_type_id: formData.packaging_type_id ? parseInt(formData.packaging_type_id) : null,
        uses_batteries: !!formData.uses_batteries,
        batteries_included: formData.uses_batteries ? !!formData.batteries_included : false,
        recommended_age: formData.recommended_age?.trim() || null,
        battery_type_id: formData.battery_type_id ? parseInt(formData.battery_type_id) : null,
        accessories: formData.accessories?.trim() || null,
        added_date: formData.added_date || null,
        inner_ctn: formData.inner_ctn !== '' ? parseInt(formData.inner_ctn, 10) : null,
        factory_id: formData.factory_id ? parseInt(formData.factory_id) : null,
        agent_id: formData.agent_id ? parseInt(formData.agent_id) : null,
        manufacturer_code: formData.manufacturer_code?.trim() || null,
        main_image: formData.main_image || null,
        gallery_images: formData.gallery_images || [],
        video_url: formData.video_url || null,
        product_improvements: formData.product_improvements?.trim() || null,
        ecommerce_description: formData.ecommerce_description?.trim() || null,
        ce_logo: !!formData.ce_logo,
        ukca_logo: !!formData.ukca_logo,
        cert_en71: formData.cert_en71 || null,
        cert_ftalatos: formData.cert_ftalatos || null,
        cert_tolueno: formData.cert_tolueno || null,
        cert_benceno: formData.cert_benceno || null,
        ean: formData.ean?.trim() || null,
        dun: formData.dun?.trim() || null,
        product_height: formData.product_height !== '' ? parseFloat(formData.product_height) : null,
        product_length: formData.product_length !== '' ? parseFloat(formData.product_length) : null,
        product_width: formData.product_width !== '' ? parseFloat(formData.product_width) : null,
        master_pack_height: formData.master_pack_height !== '' ? parseFloat(formData.master_pack_height) : null,
        master_pack_length: formData.master_pack_length !== '' ? parseFloat(formData.master_pack_length) : null,
        master_pack_width: formData.master_pack_width !== '' ? parseFloat(formData.master_pack_width) : null,
        master_pack_gross_weight: formData.master_pack_gross_weight !== '' ? parseFloat(formData.master_pack_gross_weight) : null,
        master_pack_net_weight: formData.master_pack_net_weight !== '' ? parseFloat(formData.master_pack_net_weight) : null,
        units_per_master: formData.units_per_master !== '' ? parseInt(formData.units_per_master, 10) : null,
        original_factory_cost: formData.original_factory_cost !== '' ? parseFloat(formData.original_factory_cost) : null,
        best_cost_with_order: formData.best_cost_with_order !== '' ? parseFloat(formData.best_cost_with_order) : null,
        moq: formData.moq !== '' ? parseInt(formData.moq, 10) : null,
        moq_oem: formData.moq_oem !== '' ? parseInt(formData.moq_oem, 10) : null,
        moq_factory_pack: formData.moq_factory_pack !== '' ? parseInt(formData.moq_factory_pack, 10) : null,
        logistics_factor: formData.logistics_factor !== '' ? parseFloat(formData.logistics_factor) : null,
        discount_fob: formData.discount_fob !== '' ? parseFloat(formData.discount_fob) : null,
      };

      const saved = editingProduct
        ? await onUpdateProduct(editingProduct.id, productData)
        : await onAddProduct(productData);
      const savedId = editingProduct?.id || saved?.id;
      if (savedId && formData.listPrices) {
        await Promise.all(
          Object.entries(formData.listPrices).map(([listId, price]) =>
            catalogService.priceLists.upsertProductPrice(savedId, listId, parseFloat(price) || 0)
          )
        );
      }
      
      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error('Error guardando producto:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Error desconocido';
      alert(`No se pudo guardar el producto.\n${parseErrorMessage(error, 'Revisá los datos obligatorios e intentá de nuevo.')}`);
    }
  };

  const handleClone = async () => {
    if (!editingProduct) return;
    try {
      const productData = {
        name: formData.name.trim(),
        product_code: formData.product_code?.trim() || null,
        description: formData.description?.trim() || null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        category: formData.category?.trim() || null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        product_line_id: formData.product_line_id ? parseInt(formData.product_line_id) : null,
        tag_ids: Array.isArray(formData.tag_ids) ? formData.tag_ids.map(id => parseInt(id)) : [],
        brand_type: formData.brand_type || 'GENERIC',
        product_status: 'DRAFT',
        is_for_sale: !!formData.is_for_sale,
        is_purchasable: !!formData.is_purchasable,
        unit_price: parseFloat(formData.unit_price) || 0,
        price_currency: formData.price_currency || 'USD',
        cost: parseFloat(formData.cost) || 0,
        cost_currency: formData.cost_currency || 'USD',
        profit_margin: parseFloat(formData.profit_margin) || 0,
        cost_percentage: parseFloat(formData.cost_percentage) || 0,
        unit: formData.unit || 'unidad',
        stock: 0,
        incoterm: formData.incoterm || 'FOB',
        cost_rmb: formData.cost_rmb !== '' ? parseFloat(formData.cost_rmb) : null,
        cost_fob: formData.cost_fob !== '' ? parseFloat(formData.cost_fob) : null,
        exchange_rate: formData.exchange_rate !== '' ? parseFloat(formData.exchange_rate) : null,
        price_fob: formData.price_fob !== '' ? parseFloat(formData.price_fob) : null,
        pcs_per_ctn: formData.pcs_per_ctn !== '' ? parseInt(formData.pcs_per_ctn, 10) : null,
        cbm_per_ctn: formData.cbm_per_ctn !== '' ? parseFloat(formData.cbm_per_ctn) : null,
        factory_port: formData.factory_port ? parseInt(formData.factory_port, 10) : null,
        shipping_port: formData.shipping_port ? parseInt(formData.shipping_port, 10) : null,
        is_service: formData.is_service || false,
        is_active: 'true',
        assortment: formData.assortment?.trim() || null,
        pieces: formData.pieces !== '' ? parseInt(formData.pieces, 10) : null,
        material_composition: formData.material_composition?.trim() || null,
        size: formData.size?.trim() || null,
        packaging_type_id: formData.packaging_type_id ? parseInt(formData.packaging_type_id) : null,
        uses_batteries: !!formData.uses_batteries,
        batteries_included: formData.uses_batteries ? !!formData.batteries_included : false,
        recommended_age: formData.recommended_age?.trim() || null,
        battery_type_id: formData.battery_type_id ? parseInt(formData.battery_type_id) : null,
        accessories: formData.accessories?.trim() || null,
        added_date: new Date().toISOString().slice(0, 10),
        inner_ctn: formData.inner_ctn !== '' ? parseInt(formData.inner_ctn, 10) : null,
        factory_id: formData.factory_id ? parseInt(formData.factory_id) : null,
        agent_id: formData.agent_id ? parseInt(formData.agent_id) : null,
        manufacturer_code: formData.manufacturer_code?.trim() || null,
        main_image: null,
        gallery_images: [],
        video_url: null,
        product_improvements: formData.product_improvements?.trim() || null,
        ecommerce_description: formData.ecommerce_description?.trim() || null,
        ce_logo: !!formData.ce_logo,
        ukca_logo: !!formData.ukca_logo,
        cert_en71: formData.cert_en71 || null,
        cert_ftalatos: formData.cert_ftalatos || null,
        cert_tolueno: formData.cert_tolueno || null,
        cert_benceno: formData.cert_benceno || null,
        ean: null,
        dun: null,
        product_height: formData.product_height !== '' ? parseFloat(formData.product_height) : null,
        product_length: formData.product_length !== '' ? parseFloat(formData.product_length) : null,
        product_width: formData.product_width !== '' ? parseFloat(formData.product_width) : null,
        master_pack_height: formData.master_pack_height !== '' ? parseFloat(formData.master_pack_height) : null,
        master_pack_length: formData.master_pack_length !== '' ? parseFloat(formData.master_pack_length) : null,
        master_pack_width: formData.master_pack_width !== '' ? parseFloat(formData.master_pack_width) : null,
        master_pack_gross_weight: formData.master_pack_gross_weight !== '' ? parseFloat(formData.master_pack_gross_weight) : null,
        master_pack_net_weight: formData.master_pack_net_weight !== '' ? parseFloat(formData.master_pack_net_weight) : null,
        units_per_master: formData.units_per_master !== '' ? parseInt(formData.units_per_master, 10) : null,
        original_factory_cost: formData.original_factory_cost !== '' ? parseFloat(formData.original_factory_cost) : null,
        best_cost_with_order: formData.best_cost_with_order !== '' ? parseFloat(formData.best_cost_with_order) : null,
        moq: formData.moq !== '' ? parseInt(formData.moq, 10) : null,
        moq_oem: formData.moq_oem !== '' ? parseInt(formData.moq_oem, 10) : null,
        moq_factory_pack: formData.moq_factory_pack !== '' ? parseInt(formData.moq_factory_pack, 10) : null,
        logistics_factor: formData.logistics_factor !== '' ? parseFloat(formData.logistics_factor) : null,
        discount_fob: formData.discount_fob !== '' ? parseFloat(formData.discount_fob) : null,
      };

      const cloned = await onAddProduct(productData);
      if (cloned) {
        handleEdit(cloned);
        setActiveSection('general');
      }
    } catch (error) {
      console.error('Error clonando producto:', error);
      alert(`No se pudo clonar el producto.\n${parseErrorMessage(error, 'Intentá de nuevo.')}`);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      product_code: product.product_code || '',
      description: product.description || '',
      category_id: product.category_id?.toString() || '',
      category: product.category || '',
      brand_id: product.brand_id?.toString() || '',
      product_line_id: product.product_line_id?.toString() || '',
      tag_ids: Array.isArray(product.tag_ids) ? product.tag_ids.map(id => id.toString()) : [],
      brand_type: product.brand_type || 'GENERIC',
      product_status: product.product_status || 'DRAFT',
      is_for_sale: product.is_for_sale !== false,
      is_purchasable: product.is_purchasable !== false,
      unit_price: product.unit_price || 0,
      price_currency: product.price_currency || 'USD',
      cost: product.cost || 0,
      cost_currency: product.cost_currency || 'USD',
      profit_margin: product.profit_margin || 0,
      cost_percentage: product.cost_percentage || 0,
      unit: product.unit || 'unidad',
      stock: product.stock || 0,
      incoterm: product.incoterm || 'FOB',
      cost_rmb: product.cost_rmb ?? '',
      cost_fob: product.cost_fob ?? '',
      exchange_rate: product.exchange_rate ?? '',
      price_fob: product.price_fob ?? '',
      pcs_per_ctn: product.pcs_per_ctn !== null && product.pcs_per_ctn !== undefined ? String(product.pcs_per_ctn) : '',
      cbm_per_ctn: product.cbm_per_ctn ?? '',
      factory_port: product.factory_port?.toString() || '',
      shipping_port: product.shipping_port?.toString() || '',
      is_service: product.is_service || false,
      is_active: product.is_active || 'true',
      available_markets: product.available_markets || '',
      assortment: product.assortment || '',
      pieces: product.pieces !== null && product.pieces !== undefined ? String(product.pieces) : '',
      material_composition: product.material_composition || '',
      size: product.size || '',
      packaging_type_id: product.packaging_type_id?.toString() || '',
      uses_batteries: product.uses_batteries || false,
      batteries_included: product.batteries_included || false,
      recommended_age: product.recommended_age || '',
      battery_type_id: product.battery_type_id?.toString() || '',
      moq: product.moq !== null && product.moq !== undefined ? String(product.moq) : '',
      moq_oem: product.moq_oem !== null && product.moq_oem !== undefined ? String(product.moq_oem) : '',
      moq_factory_pack: product.moq_factory_pack !== null && product.moq_factory_pack !== undefined ? String(product.moq_factory_pack) : '',
      logistics_factor: product.logistics_factor !== null && product.logistics_factor !== undefined ? String(product.logistics_factor) : '',
      discount_fob: product.discount_fob ?? '',
      product_variant: product.product_variant || '',
      battery_info: product.battery_info || '',
      accessories: product.accessories || '',
      added_date: product.added_date || '',
      inner_ctn: product.inner_ctn !== null && product.inner_ctn !== undefined ? String(product.inner_ctn) : '',
      factory_id: product.factory_id?.toString() || '',
      agent_id: product.agent_id?.toString() || '',
      manufacturer_code: product.manufacturer_code || '',
      main_image: product.main_image || '',
      gallery_images: product.gallery_images || [],
      video_url: product.video_url || '',
      product_improvements: product.product_improvements || '',
      ecommerce_description: product.ecommerce_description || '',
      ce_logo: product.ce_logo || false,
      ukca_logo: product.ukca_logo || false,
      cert_en71: product.cert_en71 || null,
      cert_ftalatos: product.cert_ftalatos || null,
      cert_tolueno: product.cert_tolueno || null,
      cert_benceno: product.cert_benceno || null,
      ean: product.ean || '',
      dun: product.dun || '',
      product_height: product.product_height ?? '',
      product_length: product.product_length ?? '',
      product_width: product.product_width ?? '',
      master_pack_height: product.master_pack_height ?? '',
      master_pack_length: product.master_pack_length ?? '',
      master_pack_width: product.master_pack_width ?? '',
      master_pack_gross_weight: product.master_pack_gross_weight ?? '',
      master_pack_net_weight: product.master_pack_net_weight ?? '',
      units_per_master: product.units_per_master !== null && product.units_per_master !== undefined ? String(product.units_per_master) : '',
      original_factory_cost: product.original_factory_cost ?? '',
      best_cost_with_order: product.best_cost_with_order ?? '',
      listPrices: (product.list_prices || []).reduce((acc, lp) => {
        acc[String(lp.price_list_id)] = lp.price ?? '';
        return acc;
      }, {})
    });
    setActiveSection('general');
    setPublicUrl('');
    setShowForm(true);
  };

  const handleDelete = async (product) => {
    const isActive = product.is_active === true || product.is_active === 'true';
    const action = isActive ? 'desactivar' : 'activar';
    const newState = isActive ? 'false' : 'true';
    if (window.confirm(`¿Estás seguro de que deseas ${action} este producto?`)) {
      try {
        await onUpdateProduct(product.id, { is_active: newState });
      } catch (error) {
        console.error(`Error al ${action} producto:`, error);
        alert(`No se pudo ${action} el producto.\n${parseErrorMessage(error)}`);
      }
    }
  };

  // Filtrar productos
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filtrar por estado activo/inactivo
    if (activeFilter === 'active') {
      filtered = filtered.filter(p => p.is_active === true || p.is_active === 'true');
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(p => p.is_active === false || p.is_active === 'false');
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.category?.toLowerCase().includes(searchLower)
      );
    }
    
    // Filtrar por categoría
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }
    
    return filtered;
  }, [products, searchTerm, categoryFilter, activeFilter]);

  // Productos ordenados por columna activa
  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return filteredProducts;
    return [...filteredProducts].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortConfig]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  }, []);

  const handleColDrop = useCallback((targetId) => {
    if (!dragCol || dragCol === targetId) { setDragCol(null); setDragOverCol(null); return; }
    setColumnConfig(prev => {
      const arr = [...prev];
      const fromIdx = arr.findIndex(c => c.id === dragCol);
      const toIdx   = arr.findIndex(c => c.id === targetId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const [item] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, item);
      return arr;
    });
    setDragCol(null);
    setDragOverCol(null);
  }, [dragCol]);

  const toggleColumnVisibility = useCallback((colId) => {
    setColumnConfig(prev => prev.map(c => c.id === colId ? { ...c, visible: !c.visible } : c));
  }, []);

  // Construye el objeto de update para onUpdateProduct según el campo
  const buildInlineUpdate = useCallback((field, value) => {
    switch (field) {
      case 'category':       return { category_id: value };
      case 'brand':          return { brand_id: value };
      case 'product_line':   return { product_line_id: value };
      case 'is_service':     return { is_service: value === 'true' };
      case 'is_for_sale':    return { is_for_sale: value === 'true' };
      case 'is_purchasable': return { is_purchasable: value === 'true' };
      case 'unit_price':     return { unit_price: parseFloat(value) || 0 };
      case 'cost':           return { cost: parseFloat(value) || 0 };
      case 'moq': case 'moq_oem': case 'moq_factory_pack': case 'pcs_per_ctn': case 'pieces': case 'inner_ctn':
        return { [field]: value !== '' ? parseInt(value) : null };
      case 'cbm_per_ctn': case 'cost_rmb': case 'cost_fob': case 'price_fob': case 'discount_fob':
        return { [field]: value !== '' ? parseFloat(value) : null };
      default: return { [field]: value };
    }
  }, []);

  // Guarda sin limpiar inlineEdit (usado en Tab navigation)
  const saveInlineField = useCallback(async (productId, field, value) => {
    setSavingInline(true);
    try {
      if (String(field).startsWith('pl_')) {
        const priceListId = parseInt(String(field).slice(3), 10);
        const price = parseFloat(value) || 0;
        await catalogService.priceLists.upsertProductPrice(productId, priceListId, price);
        setListPriceOverrides((prev) => ({
          ...prev,
          [String(productId)]: { ...(prev[String(productId)] || {}), [String(priceListId)]: price },
        }));
        return;
      }
      await onUpdateProduct(productId, buildInlineUpdate(field, value));
    } catch (err) {
      console.error('Error guardando campo inline:', err);
    } finally {
      setSavingInline(false);
    }
  }, [onUpdateProduct, buildInlineUpdate]);

  // Guarda y limpia el estado de edición
  const handleInlineEditSave = useCallback(async (productId, field, value) => {
    await saveInlineField(productId, field, value);
    setInlineEdit(null);
  }, [saveInlineField]);

  // Devuelve el valor actual de un campo para iniciar edición
  const getCellEditValue = useCallback((product, colId) => {
    switch (colId) {
      case 'category':    return String(product.category_id || '');
      case 'brand':       return String(product.brand_id || '');
      case 'product_line':return String(product.product_line_id || '');
      case 'is_service':  return String(product.is_service || false);
      case 'is_active':   return String(product.is_active !== false && product.is_active !== 'false');
      case 'is_for_sale': return String(product.is_for_sale !== false);
      case 'is_purchasable': return String(product.is_purchasable !== false);
      default:
        if (String(colId).startsWith('pl_')) {
          const val = getProductListPrice(product, String(colId).slice(3));
          return val != null && val !== '' ? String(val) : '';
        }
        return product[colId] != null ? String(product[colId]) : '';
    }
  }, [getProductListPrice]);

  // Columnas editables visibles en orden
  const visibleEditableColIds = useMemo(() => {
    const NON_EDIT = new Set(['photo', 'profit_margin', 'cost_percentage', 'stock', 'actions', 'list_price']);
    return columnConfig.filter(c => c.visible && !NON_EDIT.has(c.id)).map(c => c.id);
  }, [columnConfig]);

  // Navega con Tab al siguiente/anterior campo editable
  const handleTabNavigation = useCallback((productId, colId, isShift, currentValue) => {
    saveInlineField(productId, colId, currentValue);

    const cols = visibleEditableColIds;
    const colIdx = cols.indexOf(colId);
    let targetProductId = productId;
    let targetColId = null;

    if (!isShift) {
      if (colIdx < cols.length - 1) {
        targetColId = cols[colIdx + 1];
      } else {
        const rowIdx = sortedProducts.findIndex(p => p.id === productId);
        if (rowIdx < sortedProducts.length - 1) {
          targetProductId = sortedProducts[rowIdx + 1].id;
          targetColId = cols[0] ?? null;
        }
      }
    } else {
      if (colIdx > 0) {
        targetColId = cols[colIdx - 1];
      } else {
        const rowIdx = sortedProducts.findIndex(p => p.id === productId);
        if (rowIdx > 0) {
          targetProductId = sortedProducts[rowIdx - 1].id;
          targetColId = cols[cols.length - 1] ?? null;
        }
      }
    }

    if (targetColId) {
      const target = sortedProducts.find(p => p.id === targetProductId);
      if (target) {
        setFocusedRowId(targetProductId);
        setFocusedColId(targetColId);
        setInlineEdit({ productId: targetProductId, field: targetColId, value: getCellEditValue(target, targetColId) });
      }
    } else {
      setInlineEdit(null);
    }
  }, [saveInlineField, visibleEditableColIds, sortedProducts, getCellEditValue]);

  const SELECT_COLS = useMemo(() => new Set([
    'is_service', 'is_active', 'product_status', 'brand_type', 'incoterm',
    'is_for_sale', 'is_purchasable', 'category', 'brand', 'product_line',
  ]), []);

  // Manejo de teclado a nivel de fila (cuando no se está editando)
  // Simula selección de celda estilo Excel: ↑↓←→ mueven la celda activa,
  // Enter/F2/escribir directamente la editan.
  const handleRowKeyDown = useCallback((e, product) => {
    if (inlineEdit?.productId === product.id) return;

    const rowIdx = sortedProducts.findIndex(p => p.id === product.id);
    const rows = tableBodyRef.current?.querySelectorAll('tr[tabindex]');
    const cols = visibleEditableColIds;
    const activeColId = focusedColId && cols.includes(focusedColId) ? focusedColId : cols[0];

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (rowIdx < sortedProducts.length - 1) {
          setFocusedRowId(sortedProducts[rowIdx + 1].id);
          setFocusedColId(activeColId);
          rows?.[rowIdx + 1]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (rowIdx > 0) {
          setFocusedRowId(sortedProducts[rowIdx - 1].id);
          setFocusedColId(activeColId);
          rows?.[rowIdx - 1]?.focus();
        }
        break;
      case 'ArrowRight': {
        e.preventDefault();
        const idx = cols.indexOf(activeColId);
        if (idx >= 0 && idx < cols.length - 1) setFocusedColId(cols[idx + 1]);
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        const idx = cols.indexOf(activeColId);
        if (idx > 0) setFocusedColId(cols[idx - 1]);
        break;
      }
      case 'Enter':
      case 'F2': {
        e.preventDefault();
        if (activeColId) {
          setInlineEdit({ productId: product.id, field: activeColId, value: getCellEditValue(product, activeColId) });
          setFocusedRowId(product.id);
          setFocusedColId(activeColId);
        }
        break;
      }
      case 'Tab': {
        e.preventDefault();
        const col = e.shiftKey
          ? visibleEditableColIds[visibleEditableColIds.length - 1]
          : visibleEditableColIds[0];
        if (col) {
          setInlineEdit({ productId: product.id, field: col, value: getCellEditValue(product, col) });
          setFocusedRowId(product.id);
          setFocusedColId(col);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setFocusedRowId(null);
        e.currentTarget.blur();
        break;
      default:
        // Tecla de letra/número → empieza a editar la celda activa (si es de texto)
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const targetCol = activeColId && !SELECT_COLS.has(activeColId)
            ? activeColId
            : visibleEditableColIds.find(id => !SELECT_COLS.has(id));
          if (targetCol) {
            e.preventDefault();
            setInlineEdit({ productId: product.id, field: targetCol, value: e.key });
            setFocusedRowId(product.id);
            setFocusedColId(targetCol);
          }
        }
    }
  }, [inlineEdit, sortedProducts, visibleEditableColIds, getCellEditValue, SELECT_COLS, focusedColId]);

  // Obtener categorías únicas de productos (para compatibilidad)
  const productCategories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return cats.sort();
  }, [products]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // ── Renderizado de celda dinámica ──
  const renderCell = (product, col, isRowFocused = false) => {
    const isEditing = inlineEdit?.productId === product.id && inlineEdit?.field === col.id;
    const isCellFocused = isRowFocused && !isEditing && focusedColId === col.id;
    const focusRingCls = isCellFocused ? ' ring-2 ring-inset ring-blue-500 relative z-[1]' : '';

    const startEdit = (e, val) => {
      e.stopPropagation();
      setFocusedRowId(product.id);
      setFocusedColId(col.id);
      setInlineEdit({ productId: product.id, field: col.id, value: val ?? '' });
    };
    const commitEdit = () => {
      if (inlineEdit) handleInlineEditSave(product.id, inlineEdit.field, inlineEdit.value);
    };
    const cancelEdit = (e) => { if (e) e.stopPropagation(); setInlineEdit(null); };
    const ev = isEditing ? inlineEdit.value : undefined;
    const setEv = (v) => setInlineEdit(prev => ({ ...prev, value: v }));

    const onKeyDownNav = (e) => {
      if (e.key === 'Enter')  { e.preventDefault(); commitEdit(); return; }
      if (e.key === 'Escape') { cancelEdit(); return; }
      if (e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        handleTabNavigation(product.id, col.id, e.shiftKey, inlineEdit?.value ?? '');
        return;
      }
      e.stopPropagation();
    };

    const baseInput = {
      value: ev ?? '',
      onChange: (e) => setEv(e.target.value),
      onBlur: (e) => {
        // No guardar si el foco se va a otro campo editable (Tab lo maneja)
        if (!e.relatedTarget || !e.relatedTarget.closest('td')) commitEdit();
      },
      onKeyDown: onKeyDownNav,
      onClick: (e) => e.stopPropagation(),
      autoFocus: true,
      className: 'w-full px-1.5 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white',
    };

    const selectBase = {
      value: ev ?? '',
      onChange: (e) => { setEv(e.target.value); },
      onBlur: (e) => {
        if (!e.relatedTarget || !e.relatedTarget.closest('td')) commitEdit();
      },
      onClick: (e) => e.stopPropagation(),
      onKeyDown: (e) => {
        if (e.key === 'Escape') { cancelEdit(); return; }
        if (e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
          handleTabNavigation(product.id, col.id, e.shiftKey, inlineEdit?.value ?? '');
          return;
        }
        if (e.key === 'Enter') { e.preventDefault(); commitEdit(); return; }
        e.stopPropagation();
      },
      autoFocus: true,
      className: 'w-full px-1.5 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white',
    };

    const td = (children, extra = '') => (
      <td key={col.id} className={`px-2 py-1.5 whitespace-nowrap text-sm border-r border-b border-gray-300${col.align === 'right' ? ' text-right' : ''}${focusRingCls} ${extra}`}>
        {children}
      </td>
    );

    const dash = <span className="text-gray-300 select-none">—</span>;

    switch (col.id) {
      case 'name':
        return (
          <td key={col.id} className={`px-2 py-1.5 whitespace-nowrap text-sm max-w-[220px] border-r border-b border-gray-300${focusRingCls}`}
            onClick={e => startEdit(e, product.name)}>
            <div className="flex items-center gap-2 min-w-[140px]">
              <Package className="w-4 h-4 text-blue-400 flex-shrink-0" />
              {isEditing ? (
                <input {...baseInput} type="text" className={baseInput.className + ' font-semibold min-w-[100px]'} />
              ) : (
                <span className="truncate hover:text-blue-600 cursor-text" title={product.name}>
                  <span className="font-semibold text-gray-900">{product.name}</span>
                  <span className="ml-1.5 text-xs text-gray-400">#{product.id}</span>
                </span>
              )}
            </div>
          </td>
        );

      case 'photo':
        return (
          <td key={col.id} className="px-2 py-1.5 whitespace-nowrap border-r border-b border-gray-300" onClick={e => e.stopPropagation()}>
            {getProductImage(product) ? (
              <button type="button" onClick={() => setImagePreview(getProductImage(product))}
                className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:shadow-sm transition-shadow">
                <img src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="w-10 h-10 rounded-lg border border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                <Image className="w-3.5 h-3.5" />
              </div>
            )}
          </td>
        );

      case 'product_code':
        return td(isEditing
          ? <input {...baseInput} type="text" className={baseInput.className + ' font-mono'} />
          : <span className="font-mono text-xs text-gray-500 cursor-text" onClick={e => startEdit(e, product.product_code || '')}>{product.product_code || dash}</span>
        );

      case 'category':
        return td(isEditing
          ? <select {...selectBase}>
              <option value="">Sin categoría</option>
              {categories.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.full_path || c.name}</option>)}
            </select>
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.category_id || '')}>{product.category || dash}</span>
        );

      case 'brand':
        return td(isEditing
          ? <select {...selectBase}>
              <option value="">Sin marca</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.brand_id || '')}>{product.brand_name || brands.find(b => String(b.id) === String(product.brand_id))?.name || dash}</span>
        );

      case 'product_line':
        return td(isEditing
          ? <select {...selectBase}>
              <option value="">Sin línea</option>
              {productLines.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
            </select>
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.product_line_id || '')}>{productLines.find(pl => String(pl.id) === String(product.product_line_id))?.name || dash}</span>
        );

      case 'unit_price':        return (
          <td key={col.id} className={`px-2 py-1.5 whitespace-nowrap text-right text-sm cursor-text border-r border-b border-gray-300${focusRingCls}`} onClick={e => startEdit(e, product.unit_price ?? 0)}>
            {isEditing
              ? <input {...baseInput} type="number" step="0.01" min="0" className={baseInput.className + ' text-right'} />
              : <span className="font-semibold text-gray-900">{formatCurrency(product.unit_price)} <span className="text-xs font-normal text-gray-400">{product.price_currency || 'USD'}</span></span>}
          </td>
        );

      case 'list_price': {
        const lp = listPriceMap[product.id];
        if (!selectedPriceListId) return <td key={col.id} className="px-2 py-1.5 text-right text-xs text-gray-400 whitespace-nowrap border-r border-b border-gray-300">Seleccioná lista ↑</td>;
        if (!lp) return <td key={col.id} className="px-2 py-1.5 text-right text-xs text-gray-400 whitespace-nowrap border-r border-b border-gray-300">—</td>;
        const price   = lp.price;
        const cost    = lp.cost_fob || parseFloat(product.cost_fob) || 0;
        const profit  = price - cost;
        const margin  = price > 0 ? (profit / price * 100) : 0;
        const marginColor = margin >= 20 ? 'text-green-600' : margin >= 10 ? 'text-amber-600' : 'text-red-600';
        return (
          <td key={col.id} className="px-2 py-1.5 whitespace-nowrap text-right text-sm border-r border-b border-gray-300">
            <span className="font-semibold text-gray-900">{formatCurrency(price)}</span>
            {cost > 0 && <span className={`ml-1.5 text-xs font-medium ${marginColor}`}>{margin.toFixed(1)}%</span>}
          </td>
        );
      }

      case 'cost':
        return (
          <td key={col.id} className={`px-2 py-1.5 whitespace-nowrap text-right text-sm cursor-text border-r border-b border-gray-300${focusRingCls}`} onClick={e => startEdit(e, product.cost ?? 0)}>
            {isEditing
              ? <input {...baseInput} type="number" step="0.01" min="0" className={baseInput.className + ' text-right'} />
              : <span className="font-medium text-gray-700">{formatCurrency(product.cost || 0)} <span className="text-xs font-normal text-gray-400">{product.cost_currency || 'USD'}</span></span>}
          </td>
        );

      case 'profit_margin':
        return td(<div className="flex items-center justify-end gap-1"><TrendingUp className="w-3.5 h-3.5 text-green-500" /><span className="font-semibold text-green-600">{product.profit_margin?.toFixed(2) || '0.00'}%</span></div>);

      case 'cost_percentage':
        return td(<div className="flex items-center justify-end gap-1"><Percent className="w-3.5 h-3.5 text-orange-500" /><span className="font-semibold text-orange-600">{product.cost_percentage?.toFixed(2) || '0.00'}%</span></div>);

      case 'unit':
        return td(isEditing
          ? <input {...baseInput} type="text" />
          : <span className="text-gray-600 cursor-text" onClick={e => startEdit(e, product.unit || 'unidad')}>{product.unit || 'unidad'}</span>
        );

      case 'stock':
        return td(product.is_service
          ? <span className="text-xs text-gray-400 italic">N/A</span>
          : <span className={`font-semibold ${(product.stock || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>{product.stock || 0}</span>
        );

      case 'is_service':
        return td(isEditing
          ? <select {...selectBase}><option value="false">Producto</option><option value="true">Servicio</option></select>
          : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${product.is_service ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}`}
              onClick={e => startEdit(e, String(product.is_service))}>{product.is_service ? 'Servicio' : 'Producto'}</span>
        );

      case 'is_active': {
        const active = product.is_active !== false && product.is_active !== 'false';
        return td(isEditing
          ? <select {...selectBase}><option value="true">Activo</option><option value="false">Inactivo</option></select>
          : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
              onClick={e => startEdit(e, String(active))}>{active ? 'Activo' : 'Inactivo'}</span>
        );
      }

      case 'product_status': {
        const st = product.product_status || 'DRAFT';
        const psCfg = { DRAFT: ['bg-yellow-100 text-yellow-800', 'Borrador'], APPROVED: ['bg-green-100 text-green-800', 'Aprobado'], DISABLED: ['bg-gray-200 text-gray-600', 'Deshabilitado'] };
        const [psCls, psLbl] = psCfg[st] || psCfg.DRAFT;
        return td(isEditing
          ? <select {...selectBase}><option value="DRAFT">Borrador</option><option value="APPROVED">Aprobado</option><option value="DISABLED">Deshabilitado</option></select>
          : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${psCls}`} onClick={e => startEdit(e, st)}>{psLbl}</span>
        );
      }

      case 'brand_type':
        return td(isEditing
          ? <select {...selectBase}><option value="GENERIC">Genérico</option><option value="OWN">Orilat</option></select>
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.brand_type || 'GENERIC')}>{product.brand_type === 'OWN' ? 'Orilat' : 'Genérico'}</span>
        );

      case 'incoterm':
        return td(isEditing
          ? <select {...selectBase}><option value="FOB">FOB</option><option value="EXW">EXW</option></select>
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.incoterm || 'FOB')}>{product.incoterm || 'FOB'}</span>
        );

      case 'moq': case 'moq_oem': case 'moq_factory_pack': case 'pcs_per_ctn': case 'pieces': case 'inner_ctn':
        return td(isEditing
          ? <input {...baseInput} type="number" step="1" min="0" className={baseInput.className + ' text-right'} />
          : <span className="text-gray-600 cursor-text" onClick={e => startEdit(e, product[col.id] ?? '')}>{product[col.id] != null ? product[col.id] : dash}</span>
        );

      case 'cbm_per_ctn': case 'cost_rmb': case 'cost_fob': case 'price_fob': case 'discount_fob':
        return td(isEditing
          ? <input {...baseInput} type="number" step="0.0001" min="0" className={baseInput.className + ' text-right'} />
          : <span className="text-gray-600 cursor-text" onClick={e => startEdit(e, product[col.id] ?? '')}>{product[col.id] != null ? Number(product[col.id]).toFixed(4).replace(/\.?0+$/, '') : dash}</span>
        );

      case 'is_for_sale': case 'is_purchasable':
        return td(isEditing
          ? <select {...selectBase}><option value="true">Sí</option><option value="false">No</option></select>
          : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer ${product[col.id] !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
              onClick={e => startEdit(e, String(product[col.id] !== false))}>{product[col.id] !== false ? 'Sí' : 'No'}</span>
        );

      case 'added_date':
        return td(isEditing
          ? <input {...baseInput} type="date" />
          : <span className="text-xs text-gray-600 cursor-text" onClick={e => startEdit(e, product.added_date || '')}>{product.added_date || dash}</span>
        );

      case 'manufacturer_code': case 'size': case 'assortment': case 'material_composition':
      case 'recommended_age':   case 'ean': case 'dun':           case 'product_variant':
        return td(isEditing
          ? <input {...baseInput} type="text" />
          : <span className="text-xs text-gray-600 cursor-text truncate max-w-[140px] block" onClick={e => startEdit(e, product[col.id] || '')} title={product[col.id] || ''}>{product[col.id] || dash}</span>
        );

      case 'actions':
        return (
          <td key={col.id} className="px-2 py-1.5 whitespace-nowrap text-center border-b border-gray-300" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1">
              <button onClick={() => handleEdit(product)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar producto">
                <Edit className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(product)} className={`p-1.5 rounded-lg transition-colors ${(product.is_active === true || product.is_active === 'true') ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`} title={(product.is_active === true || product.is_active === 'true') ? 'Desactivar producto' : 'Activar producto'}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </td>
        );

      default:
        if (String(col.id).startsWith('pl_')) {
          const listId = String(col.id).slice(3);
          const price = getProductListPrice(product, listId);
          return (
            <td key={col.id} className={`px-2 py-1.5 whitespace-nowrap text-right text-sm cursor-text border-r border-b border-gray-300${focusRingCls}`} onClick={(e) => startEdit(e, price === '' ? '' : price)}>
              {isEditing
                ? <input {...baseInput} type="number" step="0.01" min="0" className={baseInput.className + ' text-right'} />
                : <span className="font-semibold text-gray-900">{price !== '' && price != null ? formatCurrency(price) : dash}</span>}
            </td>
          );
        }
        return <td key={col.id} className="px-2 py-1.5 text-sm border-r border-b border-gray-300">{dash}</td>;
    }
  };

  // ── Exportar productos a Excel ──
  const handleExportExcel = useCallback(() => {
    try {
      const getCatalogName = (list, id) => {
        if (!id) return '';
        const item = list.find(i => String(i.id) === String(id));
        return item ? item.name : '';
      };

      const boolExport = (v) => v ? 'Sí' : 'No';

      const EXAMPLE_ROW = {
        'ID': '',
        'Código': 'PROD-001',
        'Nombre': 'Ejemplo: Pelota de Fútbol',
        'Descripción': 'Pelota de fútbol tamaño 5, cuero sintético',
        'Categoría': 'Deportes',
        'Marca': 'Nike',
        'Línea de Producto': 'Outdoor',
        'Tipo Marca': 'GENERIC',
        'Estado Producto': 'ACTIVE',
        'Para Venta': 'Sí',
        'Para Compra': 'Sí',
        'Es Servicio': 'No',
        'Activo': 'Sí',
        'Precio Unitario (USD)': 25.00,
        'Costo (USD)': 15.00,
        'Costo (%)': 0,
        'Margen (%)': 40,
        'Stock': 100,
        'Unidad': 'unidad',
        'Incoterm': 'FOB',
        'Costo RMB': '120',
        'Costo FOB': '15.00',
        'Precio FOB': '25.00',
        'Descuento FOB (%)': '5',
        'Costo Original Fábrica': '12.00',
        'Mejor Costo con Pedido': '11.50',
        'Factor Logístico': '1.15',
        'PCS/CTN': '12',
        'CBM/CTN': '0.045',
        'Puerto Fábrica': 'Ningbo',
        'Puerto Embarque': 'Shanghai',
        'Fábrica': 'Fábrica Ejemplo SA',
        'ID Agente': '',
        'Código Fabricante': 'FAB-001',
        'MOQ': '500',
        'MOQ OEM': '1000',
        'MOQ Empaque Fábrica': '200',
        'Variante': 'Blanco/Negro',
        'Surtido': '',
        'Piezas': '1',
        'Inner CTN': '6',
        'Unidades por Master': '12',
        'Composición Material': 'PVC 80%, Nylon 20%',
        'Tamaño': 'Talla 5',
        'EAN': '7890000000001',
        'DUN': '17890000000008',
        'Alto Producto (cm)': '22',
        'Largo Producto (cm)': '22',
        'Ancho Producto (cm)': '22',
        'Alto Embalaje (cm)': '24',
        'Largo Embalaje (cm)': '24',
        'Ancho Embalaje (cm)': '24',
        'Alto Master Pack (cm)': '50',
        'Largo Master Pack (cm)': '48',
        'Ancho Master Pack (cm)': '48',
        'Peso Bruto Master Pack (kg)': '6.5',
        'Peso Neto Master Pack (kg)': '6.0',
        'Tipo Embalaje': 'Caja individual',
        'Electronic': 'No',
        'Pilas Incluidas': 'No',
        'Tipo Pila': '',
        'Info Batería': '',
        'Edad Recomendada': '+3 años',
        'Logo CE': 'Sí',
        'Logo UKCA': 'No',
        'Accesorios': 'Bolsa de red incluida',
        'Fecha Alta': new Date().toISOString().slice(0, 10),
        'Descripción E-commerce': 'Pelota oficial tamaño 5 ideal para partidos y entrenamiento',
        'Mejoras del Producto': '',
        'Imagen Principal': 'https://ejemplo.com/imagen.jpg',
        'Video URL': '',
      };

      const rows = products.length > 0
        ? products.map(p => ({
        'ID': p.id || '',
        'Código': p.product_code || '',
        'Nombre': p.name || '',
        'Descripción': (p.description || '').slice(0, 32000),
        'Categoría': p.category || getCatalogName(categories, p.category_id),
        'Marca': getCatalogName(brands, p.brand_id) || p.brand_name || '',
        'Línea de Producto': getCatalogName(productLines, p.product_line_id) || '',
        'Tipo Marca': p.brand_type || 'GENERIC',
        'Estado Producto': p.product_status || 'DRAFT',
        'Para Venta': boolExport(p.is_for_sale !== false),
        'Para Compra': boolExport(p.is_purchasable !== false),
        'Es Servicio': boolExport(p.is_service),
        'Activo': boolExport(p.is_active === true || p.is_active === 'true'),
        'Precio Unitario (USD)': p.unit_price || 0,
        'Costo (USD)': p.cost || 0,
        'Costo (%)': p.cost_percentage || 0,
        'Margen (%)': p.profit_margin || 0,
        'Stock': p.stock || 0,
        'Unidad': p.unit || 'unidad',
        'Incoterm': p.incoterm || '',
        'Costo RMB': p.cost_rmb || '',
        'Costo FOB': p.cost_fob || '',
        'Precio FOB': p.price_fob || '',
        'Descuento FOB (%)': p.discount_fob || '',
        'Costo Original Fábrica': p.original_factory_cost || '',
        'Mejor Costo con Pedido': p.best_cost_with_order || '',
        'Factor Logístico': p.logistics_factor || '',
        'PCS/CTN': p.pcs_per_ctn || '',
        'CBM/CTN': p.cbm_per_ctn || '',
        'Puerto Fábrica': getCatalogName(ports, p.factory_port) || '',
        'Puerto Embarque': getCatalogName(ports, p.shipping_port) || '',
        'Fábrica': factoryContacts.find(fc => fc.id === p.factory_id)?.name || p.factory_name || '',
        'ID Agente': p.agent_id || '',
        'Código Fabricante': p.manufacturer_code || '',
        'MOQ': p.moq || '',
        'MOQ OEM': p.moq_oem || '',
        'MOQ Empaque Fábrica': p.moq_factory_pack || '',
        'Variante': p.product_variant || '',
        'Surtido': p.assortment || '',
        'Piezas': p.pieces || '',
        'Inner CTN': p.inner_ctn || '',
        'Unidades por Master': p.units_per_master || '',
        'Composición Material': p.material_composition || '',
        'Tamaño': p.size || '',
        'EAN': p.ean || '',
        'DUN': p.dun || '',
        'Alto Producto (cm)': p.product_height || '',
        'Largo Producto (cm)': p.product_length || '',
        'Ancho Producto (cm)': p.product_width || '',
        'Alto Embalaje (cm)': p.packaging_height || '',
        'Largo Embalaje (cm)': p.packaging_length || '',
        'Ancho Embalaje (cm)': p.packaging_width || '',
        'Alto Master Pack (cm)': p.master_pack_height || '',
        'Largo Master Pack (cm)': p.master_pack_length || '',
        'Ancho Master Pack (cm)': p.master_pack_width || '',
        'Peso Bruto Master Pack (kg)': p.master_pack_gross_weight || '',
        'Peso Neto Master Pack (kg)': p.master_pack_net_weight || '',
        'Tipo Embalaje': getCatalogName(packagingTypes, p.packaging_type_id) || '',
        'Electronic': boolExport(p.uses_batteries),
        'Pilas Incluidas': boolExport(p.batteries_included),
        'Tipo Pila': getCatalogName(batteryTypes, p.battery_type_id) || '',
        'Info Batería': (p.battery_info || '').slice(0, 32000),
        'Edad Recomendada': p.recommended_age || '',
        'Logo CE': boolExport(p.ce_logo),
        'Logo UKCA': boolExport(p.ukca_logo),
        'Accesorios': (p.accessories || '').slice(0, 32000),
        'Fecha Alta': p.added_date || '',
        'Descripción E-commerce': (p.ecommerce_description || '').slice(0, 32000),
        'Mejoras del Producto': (p.product_improvements || '').slice(0, 32000),
        'Imagen Principal': (() => {
          const img = p.main_image || '';
          if (img.startsWith('data:')) return '(imagen base64 no exportable)';
          return img.length > 32000 ? img.slice(0, 32000) : img;
        })(),
        'Video URL': p.video_url || '',
      }))
        : [EXAMPLE_ROW];

      const ws = XLSX.utils.json_to_sheet(rows);

      // Si es fila de ejemplo, resaltar en amarillo
      if (products.length === 0) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let C = range.s.c; C <= range.e.c; C++) {
          const cell = ws[XLSX.utils.encode_cell({ r: 1, c: C })];
          if (cell) {
            cell.s = {
              fill: { fgColor: { rgb: 'FFF9C4' } },
              font: { italic: true, color: { rgb: '795548' } },
            };
          }
        }
      }
      // Ajustar ancho de columnas
      const colWidths = Object.keys(rows[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 14) }));
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      XLSX.writeFile(wb, `catalogo_productos_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Error exportando productos:', error);
      alert('No se pudo exportar el catálogo. Intentá de nuevo.');
    }
  }, [products, categories, brands, productLines, factories, factoryContacts, packagingTypes, batteryTypes, ports]);

  // ── Importar productos desde Excel ──
  const handleImportExcel = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          if (jsonData.length === 0) {
            reject(new Error('El archivo no contiene datos'));
            return;
          }

          const boolVal = (v) => {
            if (typeof v === 'boolean') return v;
            const s = String(v).toLowerCase().trim();
            return s === 'sí' || s === 'si' || s === 'true' || s === '1' || s === 'yes';
          };

          // Busca un elemento en una lista por nombre (case-insensitive), retorna el id como string o ''
          const findCatalogId = (list, name) => {
            if (!name) return '';
            const n = String(name).toLowerCase().trim();
            const match = list.find(i => i.name && i.name.toLowerCase().trim() === n);
            return match ? String(match.id) : '';
          };

          const findCategoryId = (name) => {
            if (!name) return '';
            const n = String(name).toLowerCase().trim();
            const match = categories.find(c => c.name && c.name.toLowerCase().trim() === n);
            return match ? String(match.id) : '';
          };

          // Busca fábrica por nombre (case-insensitive) en factoryContacts primero, luego en factories
          const findFactoryId = (name) => {
            if (!name) return '';
            const n = String(name).toLowerCase().trim();
            const contactMatch = factoryContacts.find(fc => fc.name && fc.name.toLowerCase().trim() === n);
            if (contactMatch) return String(contactMatch.id);
            return findCatalogId(factories, name);
          };

          let created = 0, updated = 0, errors = [];
          const productWarnings = []; // advertencias por producto (campos no resueltos)

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            const rowNum = i + 2; // +2 porque fila 1 es header
            try {
              const numOrEmpty = (v) => (v !== undefined && v !== '') ? (parseFloat(v) || '') : '';
              const intOrEmpty = (v) => (v !== undefined && v !== '') ? (parseInt(v) || '') : '';

              // Resolver campos FK por nombre y registrar advertencias si no se encuentran
              const fieldWarnings = [];

              const resolvedCategoryId = findCategoryId(row['Categoría']);
              if (row['Categoría'] && !resolvedCategoryId) fieldWarnings.push(`Categoría "${row['Categoría']}" no encontrada en el sistema`);

              const resolvedBrandId = findCatalogId(brands, row['Marca']);
              if (row['Marca'] && !resolvedBrandId) fieldWarnings.push(`Marca "${row['Marca']}" no encontrada en el sistema`);

              const resolvedProductLineId = findCatalogId(productLines, row['Línea de Producto']);
              if (row['Línea de Producto'] && !resolvedProductLineId) fieldWarnings.push(`Línea de Producto "${row['Línea de Producto']}" no encontrada en el sistema`);

              const resolvedPackagingTypeId = findCatalogId(packagingTypes, row['Tipo Embalaje']);
              if (row['Tipo Embalaje'] && !resolvedPackagingTypeId) fieldWarnings.push(`Tipo de Embalaje "${row['Tipo Embalaje']}" no encontrado en el sistema`);

              const resolvedBatteryTypeId = findCatalogId(batteryTypes, row['Tipo Pila']);
              if (row['Tipo Pila'] && !resolvedBatteryTypeId) fieldWarnings.push(`Tipo de Pila "${row['Tipo Pila']}" no encontrado en el sistema`);

              const resolvedFactoryId = findFactoryId(row['Fábrica']);
              if (row['Fábrica'] && !resolvedFactoryId) fieldWarnings.push(`Fábrica "${row['Fábrica']}" no encontrada en el sistema`);

              const resolvedFactoryPortId = findCatalogId(ports, row['Puerto Fábrica']);
              if (row['Puerto Fábrica'] && !resolvedFactoryPortId) fieldWarnings.push(`Puerto Fábrica "${row['Puerto Fábrica']}" no encontrado en el sistema`);

              const resolvedShippingPortId = findCatalogId(ports, row['Puerto Embarque']);
              if (row['Puerto Embarque'] && !resolvedShippingPortId) fieldWarnings.push(`Puerto Embarque "${row['Puerto Embarque']}" no encontrado en el sistema`);

              const productData = {
                name: row['Nombre'] || '',
                product_code: row['Código'] || '',
                description: row['Descripción'] || '',
                category_id: resolvedCategoryId,
                brand_id: resolvedBrandId,
                product_line_id: resolvedProductLineId,
                brand_type: row['Tipo Marca'] || 'GENERIC',
                product_status: row['Estado Producto'] || 'DRAFT',
                is_for_sale: row['Para Venta'] !== undefined ? boolVal(row['Para Venta']) : true,
                is_purchasable: row['Para Compra'] !== undefined ? boolVal(row['Para Compra']) : true,
                is_service: boolVal(row['Es Servicio']),
                is_active: row['Activo'] !== undefined ? (boolVal(row['Activo']) ? 'true' : 'false') : 'true',
                unit_price: parseFloat(row['Precio Unitario (USD)']) || 0,
                cost: parseFloat(row['Costo (USD)']) || 0,
                cost_percentage: numOrEmpty(row['Costo (%)']),
                profit_margin: parseFloat(row['Margen (%)']) || 0,
                stock: parseInt(row['Stock']) || 0,
                unit: row['Unidad'] || 'unidad',
                incoterm: row['Incoterm'] || 'FOB',
                cost_rmb: numOrEmpty(row['Costo RMB']),
                cost_fob: numOrEmpty(row['Costo FOB']),
                price_fob: numOrEmpty(row['Precio FOB']),
                discount_fob: numOrEmpty(row['Descuento FOB (%)']),
                original_factory_cost: numOrEmpty(row['Costo Original Fábrica']),
                best_cost_with_order: numOrEmpty(row['Mejor Costo con Pedido']),
                logistics_factor: numOrEmpty(row['Factor Logístico']),
                pcs_per_ctn: intOrEmpty(row['PCS/CTN']),
                cbm_per_ctn: numOrEmpty(row['CBM/CTN']),
                factory_port: resolvedFactoryPortId,
                shipping_port: resolvedShippingPortId,
                factory_id: resolvedFactoryId,
                agent_id: row['ID Agente'] ? parseInt(row['ID Agente']) || '' : '',
                manufacturer_code: row['Código Fabricante'] || '',
                moq: intOrEmpty(row['MOQ']),
                moq_oem: intOrEmpty(row['MOQ OEM']),
                moq_factory_pack: intOrEmpty(row['MOQ Empaque Fábrica']),
                product_variant: row['Variante'] || '',
                assortment: row['Surtido'] || '',
                pieces: row['Piezas'] !== undefined && row['Piezas'] !== '' ? String(row['Piezas']) : '',
                inner_ctn: intOrEmpty(row['Inner CTN']),
                units_per_master: intOrEmpty(row['Unidades por Master']),
                material_composition: row['Composición Material'] || '',
                size: row['Tamaño'] || '',
                ean: row['EAN'] ? String(row['EAN']) : '',
                dun: row['DUN'] ? String(row['DUN']) : '',
                product_height: numOrEmpty(row['Alto Producto (cm)']),
                product_length: numOrEmpty(row['Largo Producto (cm)']),
                product_width: numOrEmpty(row['Ancho Producto (cm)']),
                packaging_height: numOrEmpty(row['Alto Embalaje (cm)']),
                packaging_length: numOrEmpty(row['Largo Embalaje (cm)']),
                packaging_width: numOrEmpty(row['Ancho Embalaje (cm)']),
                master_pack_height: numOrEmpty(row['Alto Master Pack (cm)']),
                master_pack_length: numOrEmpty(row['Largo Master Pack (cm)']),
                master_pack_width: numOrEmpty(row['Ancho Master Pack (cm)']),
                master_pack_gross_weight: numOrEmpty(row['Peso Bruto Master Pack (kg)']),
                master_pack_net_weight: numOrEmpty(row['Peso Neto Master Pack (kg)']),
                packaging_type_id: resolvedPackagingTypeId,
                uses_batteries: boolVal(row['Electronic'] ?? row['Usa Pilas']),
                batteries_included: boolVal(row['Pilas Incluidas']),
                battery_type_id: resolvedBatteryTypeId,
                battery_info: row['Info Batería'] || '',
                recommended_age: row['Edad Recomendada'] || '',
                ce_logo: boolVal(row['Logo CE']),
                ukca_logo: boolVal(row['Logo UKCA']),
                accessories: row['Accesorios'] || '',
                added_date: row['Fecha Alta'] || '',
                ecommerce_description: row['Descripción E-commerce'] || '',
                product_improvements: row['Mejoras del Producto'] || '',
                main_image: row['Imagen Principal'] || '',
                video_url: row['Video URL'] || '',
              };

              if (!productData.name) {
                errors.push(`Fila ${rowNum}: Falta el campo "Nombre" (obligatorio)`);
                continue;
              }

              const existingId = row['ID'] ? String(row['ID']).trim() : '';
              if (existingId) {
                await onUpdateProduct(existingId, productData);
                updated++;
              } else {
                await onAddProduct(productData);
                created++;
              }

              if (fieldWarnings.length > 0) {
                productWarnings.push({
                  row: rowNum,
                  name: productData.name,
                  action: existingId ? 'actualizado' : 'creado',
                  warnings: fieldWarnings,
                });
              }
            } catch (err) {
              errors.push(`Fila ${rowNum}: ${err.message || 'Error desconocido'}`);
            }
          }

          resolve({ total: jsonData.length, created, updated, errors, warnings: productWarnings });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Error leyendo archivo'));
      reader.readAsArrayBuffer(file);
    });
  }, [categories, brands, productLines, factories, factoryContacts, packagingTypes, batteryTypes, ports, onAddProduct, onUpdateProduct]);

  return (
    <div className="space-y-4">
      {showProductImport ? (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setShowProductImport(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Volver a Producto
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">Importar / Exportar Productos</span>
          </div>
          <ProductImportModal
            onClose={() => setShowProductImport(false)}
            onImport={handleImportExcel}
            onExport={handleExportExcel}
          />
        </div>
      ) : !showForm ? (
      <>
      {/* Header */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Producto
            </h1>
            <p className="text-gray-600 mt-1">Gestiona los productos para cotizaciones</p>
          </div>
          <div className="flex gap-3">
            <motion.button
              onClick={handleExportExcel}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FileSpreadsheet className="w-5 h-5" />
              Exportar Excel
            </motion.button>
            <motion.button
              onClick={() => setShowProductImport(true)}
              className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Upload className="w-5 h-5" />
              Importar Excel
            </motion.button>
            <motion.button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-5 h-5" />
              Nuevo Producto
            </motion.button>
          </div>
        </div>

        {/* Filtros */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          >
            <option value="all">Todas las categorías</option>
            {categories.filter(c => c.is_active).map(cat => (
              <option key={cat.id} value={cat.full_path || cat.name}>{'─'.repeat(cat.level || 0)} {cat.full_path || cat.name}</option>
            ))}
            {productCategories.filter(cat => !categories.find(c => (c.full_path || c.name) === cat)).map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          {/* Botón configurar columnas */}
          <div ref={columnPanelRef}>
            <button
              ref={columnBtnRef}
              onClick={() => {
                if (!showColumnPanel && columnBtnRef.current) {
                  const rect = columnBtnRef.current.getBoundingClientRect();
                  setPanelPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                }
                setShowColumnPanel(v => !v);
              }}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${showColumnPanel ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              title="Configurar columnas"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Columnas
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">
                {columnConfig.filter(c => c.visible).length}
              </span>
            </button>
          </div>
          {showColumnPanel && (
            <div
              ref={floatingPanelRef}
              style={{ position: 'fixed', top: panelPos.top, right: panelPos.right, zIndex: 9999 }}
              className="w-64 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden"
            >
              {/* Header con OK */}
              <div className="flex items-center justify-between px-3 pt-3 pb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Columnas visibles</span>
                <button
                  onClick={() => setShowColumnPanel(false)}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-colors flex items-center gap-1"
                >
                  <Check className="w-3 h-3" /> OK
                </button>
              </div>
              {/* Todas / Ninguna */}
              <div className="flex gap-2 px-3 pb-2">
                <button
                  onClick={() => setColumnConfig(prev => prev.map(c => ({ ...c, visible: true })))}
                  className="flex-1 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors"
                >
                  Todas
                </button>
                <button
                  onClick={() => setColumnConfig(prev => prev.map(c => ({ ...c, visible: c.id === 'name' || c.id === 'actions' })))}
                  className="flex-1 text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium transition-colors"
                >
                  Ninguna
                </button>
              </div>
              {/* Lista de columnas */}
              <div className="border-t border-gray-100 px-3 pb-3 pt-2 space-y-0.5 overflow-y-auto" style={{ maxHeight: Math.min(320, window.innerHeight - panelPos.top - 80) }}>
                {columnConfig.map((cc) => {
                  const def = allColumnDefs.find(c => c.id === cc.id);
                  if (!def) return null;
                  return (
                    <label key={cc.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cc.visible}
                        onChange={() => toggleColumnVisibility(cc.id)}
                        className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 accent-blue-600"
                      />
                      <span className="text-sm text-gray-700">{def.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista de productos */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-xl">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No hay productos disponibles</p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm || categoryFilter !== 'all' 
              ? 'Intenta con otros filtros' 
              : 'Crea tu primer producto para comenzar'}
          </p>
        </div>
      ) : viewMode === 'list' ? (
        /* Vista de Lista — columnas dinámicas */
        (() => {
          const visibleColDefs = columnConfig
            .filter(c => c.visible)
            .map(c => allColumnDefs.find(col => col.id === c.id))
            .filter(Boolean);

          return (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* ── Tabs selector de lista de precios ── */}
              {priceLists.length > 0 && (
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 font-medium mr-1">Lista de precios:</span>
                  <button
                    type="button"
                    onClick={() => setSelectedPriceListId(null)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${!selectedPriceListId ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    Ninguna
                  </button>
                  {priceLists.map(pl => (
                    <button
                      key={pl.id}
                      type="button"
                      onClick={() => setSelectedPriceListId(String(pl.id))}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedPriceListId === String(pl.id) ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                    >
                      {pl.name}
                    </button>
                  ))}
                </div>
              )}

              {savingInline && (
                <div className="px-4 py-1.5 bg-blue-50 border-b border-blue-100 text-xs text-blue-600 flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  Guardando…
                </div>
              )}
              <div className="overflow-auto max-h-[70vh] border-t border-gray-200">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100">
                      {visibleColDefs.map((col) => {
                        const isDragging = dragCol === col.id;
                        const isDragOver = dragOverCol === col.id;
                        return (
                          <th
                            key={col.id}
                            draggable
                            onDragStart={() => setDragCol(col.id)}
                            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                            onDrop={() => handleColDrop(col.id)}
                            onDragEnd={() => { setDragCol(null); setDragOverCol(null); }}
                            onClick={() => col.sortKey && handleSort(col.sortKey)}
                            className={`px-2 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider select-none whitespace-nowrap
                              border-r border-b-2 border-gray-300 bg-gray-100
                              ${col.align === 'right' ? 'text-right' : ''}
                              ${col.sortKey ? 'cursor-pointer hover:bg-gray-200' : 'cursor-grab'}
                              ${isDragOver ? 'bg-blue-50 border-l-2 border-l-blue-400' : ''}
                              ${isDragging ? 'opacity-50' : ''}
                              transition-colors`}
                          >
                            <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                              <GripVertical className="w-3 h-3 text-gray-300 flex-shrink-0" />
                              {col.label}
                              {col.sortKey && sortConfig.key === col.sortKey && (
                                sortConfig.direction === 'asc'
                                  ? <ArrowUp className="w-3 h-3 text-blue-500" />
                                  : <ArrowDown className="w-3 h-3 text-blue-500" />
                              )}
                              {col.sortKey && sortConfig.key !== col.sortKey && (
                                <ArrowUpDown className="w-3 h-3 text-gray-300" />
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody ref={tableBodyRef}>
                    {sortedProducts.map((product) => {
                      const isRowEditing = inlineEdit?.productId === product.id;
                      const isRowFocused = focusedRowId === product.id && !isRowEditing;
                      return (
                        <tr
                          key={product.id}
                          tabIndex={0}
                          onDoubleClick={() => handleEdit(product)}
                          onFocus={() => setFocusedRowId(product.id)}
                          onBlur={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) setFocusedRowId(null);
                          }}
                          onKeyDown={(e) => handleRowKeyDown(e, product)}
                          className={`transition-colors outline-none
                            ${isRowEditing ? 'bg-blue-50/60' : ''}
                            ${isRowFocused ? 'bg-amber-50/40' : ''}
                            ${!isRowEditing && !isRowFocused ? 'hover:bg-slate-50' : ''}
                          `}
                        >
                          {visibleColDefs.map((col) => renderCell(product, col, isRowFocused))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Barra de atajos de teclado */}
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <span className="font-medium text-gray-500">{sortedProducts.length}</span>
                  <span>producto{sortedProducts.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap">
                  {[
                    ['↑↓←→', 'navegar celdas'],
                    ['Tab', 'siguiente campo'],
                    ['Enter', 'guardar / editar'],
                    ['Esc', 'cancelar'],
                    ['F2', 'editar celda'],
                    ['Doble click', 'editar fila completa'],
                  ].map(([key, label]) => (
                    <span key={key} className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 rounded shadow-sm text-gray-600">{key}</kbd>
                      <span>{label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        /* Vista de Tarjetas */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProducts.map((product) => (
            <motion.div
              key={product.id}
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="mb-4">
                    {getProductImage(product) ? (
                      <button
                        type="button"
                        onClick={() => setImagePreview(getProductImage(product))}
                        className="w-full h-40 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 hover:shadow-sm transition-shadow"
                        title="Ver imagen"
                      >
                        <img
                          src={getProductImage(product)}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ) : (
                      <div className="w-full h-40 rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                        <Image className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{product.name}</h3>
                  {product.category && (
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-2">
                      {product.category}
                    </span>
                  )}
                  {product.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <motion.button
                    onClick={() => handleEdit(product)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Editar producto"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Edit className="w-5 h-5" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleDelete(product)}
                    className={`p-2 rounded-lg transition-colors ${(product.is_active === true || product.is_active === 'true') ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                    title={(product.is_active === true || product.is_active === 'true') ? 'Desactivar producto' : 'Activar producto'}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Trash2 className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              <div className="space-y-2 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Precio:</span>
                  <span className="text-lg font-bold text-gray-800">{formatCurrency(product.unit_price)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Costo:</span>
                  <span className="text-sm font-semibold text-gray-700">{formatCurrency(product.cost || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    Margen:
                  </span>
                  <span className="text-sm font-semibold text-green-600">{product.profit_margin?.toFixed(2) || '0.00'}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 flex items-center gap-1">
                    <Percent className="w-4 h-4" />
                    Costo %:
                  </span>
                  <span className="text-sm font-semibold text-orange-600">{product.cost_percentage?.toFixed(2) || '0.00'}%</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      </>
      ) : null}

      {showCatalogManager && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <span className="text-base font-bold text-gray-800">Gestionar catálogo</span>
              <button
                onClick={() => setShowCatalogManager(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              <CatalogsManager initialTab={catalogManagerTab} />
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => { resetForm(); setShowForm(false); }} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Volver a Producto
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</span>
            {editingProduct && formData.name && (
              <>
                <span className="text-sm text-gray-400">/</span>
                <span className="text-sm font-semibold text-blue-700">{formData.name}</span>
              </>
            )}
            {editingProduct && (
              <button
                type="button"
                onClick={handleClone}
                className="ml-auto px-4 py-2 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Clonar
              </button>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
                  {[
                    { key: 'general', label: 'General' },
                    { key: 'costo', label: 'Costo/Logística' },
                    { key: 'factory', label: 'Información de Fábrica' },
                    { key: 'multimedia', label: 'Multimedia - Diseño & Producto' },
                  ].map(section => (
                    <button
                      key={section.key}
                      type="button"
                      onClick={() => setActiveSection(section.key)}
                      className={`px-3 py-1.5 text-sm rounded-t-lg border-b-2 transition-colors ${
                        activeSection === section.key
                          ? 'text-blue-600 border-blue-600 font-semibold'
                          : 'text-gray-500 border-transparent hover:text-gray-700'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>

                {activeSection === 'general' && (
                  <div className="space-y-4">
                    <div className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">
                      General
                    </div>

                    {/* ── Fila 1: Tipo de marca · Marca · Estado · Activo · Mercados ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de marca</label>
                        <select
                          value={formData.brand_type || 'GENERIC'}
                          onChange={(e) => {
                            const brand_type = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              brand_type,
                              brand_id: brand_type === 'GENERIC' ? '' : prev.brand_id,
                            }));
                          }}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        >
                          <option value="GENERIC">Genérico</option>
                          <option value="OWN">Orilat</option>
                        </select>
                      </div>
                      {formData.brand_type === 'OWN' && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-xs font-medium text-gray-600">Marca</label>
                            <button type="button" onClick={() => openCatalogManager('brands')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                              <Settings className="w-3 h-3" />
                            </button>
                          </div>
                          <select
                            value={formData.brand_id || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, brand_id: e.target.value }))}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          >
                            <option value="">Seleccionar</option>
                            {brands.filter(b => b.is_active).map(brand => (
                              <option key={brand.id} value={brand.id}>{brand.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Estado</label>
                        <select
                          value={formData.product_status || 'DRAFT'}
                          onChange={(e) => setFormData(prev => ({ ...prev, product_status: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        >
                          <option value="DRAFT">Borrador</option>
                          <option value="APPROVED">Aprobado</option>
                          <option value="DISABLED">Deshabilitado</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Mercados disponibles</label>
                        <input
                          type="text"
                          value={formData.available_markets || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, available_markets: e.target.value }))}
                          placeholder="Ej: AR, BR, CL"
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* ── Fila 2: Código ítem + Código fábrica + Nombre + Categoría ── */}
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Código ítem</label>
                        <input
                          type="text"
                          value={formData.product_code}
                          onChange={(e) => setFormData(prev => ({ ...prev, product_code: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cód. ítem fábrica</label>
                        <input
                          type="text"
                          value={formData.manufacturer_code}
                          onChange={(e) => setFormData(prev => ({ ...prev, manufacturer_code: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del Producto *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          required
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-600">Categoría</label>
                          <button type="button" onClick={() => setShowCategoryManager(true)} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Settings className="w-3 h-3" /> Gestionar
                          </button>
                        </div>
                        <select
                          value={formData.category_id || ''}
                          onChange={(e) => {
                            const selectedCategory = categories.find(c => c.id.toString() === e.target.value);
                            setFormData(prev => ({ ...prev, category_id: e.target.value, category: selectedCategory?.name || '' }));
                          }}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        >
                          <option value="">Sin categoría</option>
                          {categories.filter(c => c.is_active).map(cat => (
                            <option key={cat.id} value={cat.id}>{'─'.repeat(cat.level || 0)} {cat.full_path || cat.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* ── Fila 3: Fecha alta (Línea de producto oculta por ahora) ── */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de alta</label>
                        <input
                          type="date"
                          value={formData.added_date}
                          onChange={(e) => setFormData(prev => ({ ...prev, added_date: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* ── Descripción (ancho completo) ── */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* ── Fila 4: Surtido ── */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Surtido</label>
                        <input
                          type="text"
                          value={formData.assortment}
                          onChange={(e) => setFormData(prev => ({ ...prev, assortment: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                    </div>


                    {/* ── Dimensión producto (cm) ── */}
                    <div>
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dimensión producto (cm)</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Alto</label>
                          <input type="number" step="0.01" min="0" value={formData.product_height}
                            onChange={(e) => setFormData(prev => ({ ...prev, product_height: e.target.value }))}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Largo</label>
                          <input type="number" step="0.01" min="0" value={formData.product_length}
                            onChange={(e) => setFormData(prev => ({ ...prev, product_length: e.target.value }))}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Ancho</label>
                          <input type="number" step="0.01" min="0" value={formData.product_width}
                            onChange={(e) => setFormData(prev => ({ ...prev, product_width: e.target.value }))}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                        </div>
                      </div>
                    </div>

                    {/* ── Fila 6: Material · Embalaje ── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Material</label>
                        <input type="text" value={formData.material_composition}
                          onChange={(e) => setFormData(prev => ({ ...prev, material_composition: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-600">Tipo de embalaje</label>
                          <button type="button" onClick={() => openCatalogManager('packagingTypes')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Settings className="w-3 h-3" />
                          </button>
                        </div>
                        <select value={formData.packaging_type_id || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, packaging_type_id: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500">
                          <option value="">Seleccionar</option>
                          {packagingTypes.filter(p => p.is_active).map(pack => (
                            <option key={pack.id} value={pack.id}>{pack.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* ── Fila 7: Electronic · Tipo batería · Edad recomendada ── */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Electronic</label>
                        <select value={formData.uses_batteries ? 'SI' : 'NO'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            uses_batteries: e.target.value === 'SI',
                            batteries_included: e.target.value === 'SI' ? prev.batteries_included : false
                          }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500">
                          <option value="NO">NO</option>
                          <option value="SI">SI</option>
                        </select>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-gray-600">Tipo de batería</label>
                          <button type="button" onClick={() => openCatalogManager('batteryTypes')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                            <Settings className="w-3 h-3" />
                          </button>
                        </div>
                        <select value={formData.battery_type_id || ''}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const selectedBattery = batteryTypes.find(b => String(b.id) === selectedId);
                            setFormData(prev => ({
                              ...prev,
                              battery_type_id: selectedId,
                              battery_info: selectedBattery?.description || '',
                            }));
                          }}
                          disabled={!formData.uses_batteries}
                          className={`w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${!formData.uses_batteries ? 'opacity-50' : ''}`}>
                          <option value="">Seleccionar</option>
                          {batteryTypes.filter(b => b.is_active).map(battery => (
                            <option key={battery.id} value={battery.id}>{battery.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Edad recomendada</label>
                        <input type="text" value={formData.recommended_age}
                          onChange={(e) => setFormData(prev => ({ ...prev, recommended_age: e.target.value }))}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                      </div>
                    </div>

                    {/* ── Checkboxes: a la venta / para comprar ── */}
                    <div className="flex flex-wrap gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={formData.is_for_sale}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_for_sale: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                        <span className="text-sm text-gray-700">Producto a la venta</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={formData.is_purchasable}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_purchasable: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                        <span className="text-sm text-gray-700">Producto se puede comprar</span>
                      </label>
                    </div>

                    {/* ── Accesorios / Info batería ── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Accesorios/Piezas del producto</label>
                        <textarea value={formData.accessories}
                          onChange={(e) => setFormData(prev => ({ ...prev, accessories: e.target.value }))}
                          rows={2}
                          className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Información de batería</label>
                        <textarea value={formData.battery_info}
                          readOnly
                          rows={2}
                          className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg cursor-not-allowed text-gray-600 resize-none" />
                      </div>
                    </div>

                    {/* ── Etiquetas: búsqueda + creación inline ── */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-gray-600">Etiquetas</label>
                        <button type="button" onClick={() => openCatalogManager('productTags')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          <Settings className="w-3 h-3" /> Gestionar
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-2 space-y-2">
                        {/* Tags seleccionadas */}
                        {formData.tag_ids.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {formData.tag_ids.map(tid => {
                              const tag = productTags.find(t => String(t.id) === String(tid));
                              if (!tag) return null;
                              return (
                                <span key={tid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {tag.name}
                                  <button type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, tag_ids: prev.tag_ids.filter(id => String(id) !== String(tid)) }))}
                                    className="hover:text-blue-600">
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {/* Buscador */}
                        <div className="relative">
                          <input
                            type="text"
                            value={tagSearch}
                            onChange={(e) => setTagSearch(e.target.value)}
                            placeholder="Buscar o crear etiqueta..."
                            className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                          {tagSearch.trim() && (
                            <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {productTags.filter(t => t.is_active && t.name.toLowerCase().includes(tagSearch.trim().toLowerCase()) && !formData.tag_ids.includes(String(t.id)) && !formData.tag_ids.includes(t.id)).map(tag => (
                                <button key={tag.id} type="button"
                                  onMouseDown={(e) => { e.preventDefault(); setFormData(prev => ({ ...prev, tag_ids: [...prev.tag_ids, String(tag.id)] })); setTagSearch(''); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center gap-2">
                                  <Tag className="w-3.5 h-3.5 text-gray-400" /> {tag.name}
                                </button>
                              ))}
                              {!productTags.some(t => t.name.toLowerCase() === tagSearch.trim().toLowerCase()) && (
                                <button type="button" disabled={creatingTag}
                                  onMouseDown={async (e) => {
                                    e.preventDefault();
                                    if (creatingTag) return;
                                    setCreatingTag(true);
                                    try {
                                      const created = await catalogService.productTags.create({ name: tagSearch.trim(), is_active: true });
                                      setProductTags(prev => [...prev, created]);
                                      setFormData(prev => ({ ...prev, tag_ids: [...prev.tag_ids, String(created.id)] }));
                                      setTagSearch('');
                                    } catch { /* silencioso */ } finally { setCreatingTag(false); }
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t border-gray-100">
                                  <Plus className="w-3.5 h-3.5" /> Crear etiqueta «{tagSearch.trim()}»
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Imágenes del producto: 4 slots en grid ── */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-2">
                        Imágenes del producto <span className="text-gray-400 font-normal">(máx. 4)</span>
                      </label>
                      <div className="grid grid-cols-4 gap-3">
                        {/* Slot 1: imagen principal */}
                        {(() => {
                          const imgSrc = formData.main_image;
                          return (
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-blue-600">Principal</span>
                              <div className="relative w-full aspect-square border-2 border-blue-300 rounded-xl overflow-hidden bg-gray-50 shadow-sm">
                                {imgSrc ? (
                                  <>
                                    <img
                                      src={imgSrc}
                                      alt="Principal"
                                      className="w-full h-full object-cover cursor-zoom-in"
                                      onClick={() => setImagePreview(imgSrc)}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setFormData(prev => ({ ...prev, main_image: '' }))}
                                      className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </>
                                ) : (
                                  <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors">
                                    <Image className="w-6 h-6 text-gray-300 mb-1" />
                                    <span className="text-xs text-gray-400">Subir</span>
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMainImageChange(e.target.files?.[0])} />
                                  </label>
                                )}
                              </div>
                              {imgSrc && (
                                <label className="text-xs text-blue-500 cursor-pointer hover:text-blue-700 underline">
                                  Cambiar
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMainImageChange(e.target.files?.[0])} />
                                </label>
                              )}
                            </div>
                          );
                        })()}

                        {/* Slots 2-4: gallery_images[0], [1], [2] */}
                        {[0, 1, 2].map(i => {
                          const imgSrc = (formData.gallery_images || [])[i];
                          return (
                            <div key={i} className="flex flex-col items-center gap-1">
                              <span className="text-xs font-medium text-gray-500">Foto {i + 2}</span>
                              <div className="relative w-full aspect-square border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 shadow-sm">
                                {imgSrc ? (
                                  <>
                                    <img
                                      src={imgSrc}
                                      alt={`Galería ${i + 1}`}
                                      className="w-full h-full object-cover cursor-zoom-in"
                                      onClick={() => setImagePreview(imgSrc)}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleGallerySlotRemove(i)}
                                      className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </>
                                ) : (
                                  <label className={`w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors ${(formData.gallery_images || []).length < 3 || (formData.gallery_images || [])[i] ? '' : 'opacity-50 cursor-not-allowed'}`}>
                                    <Image className="w-6 h-6 text-gray-300 mb-1" />
                                    <span className="text-xs text-gray-400">Subir</span>
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleGallerySlotChange(e.target.files?.[0], i)} />
                                  </label>
                                )}
                              </div>
                              {imgSrc && (
                                <label className="text-xs text-blue-500 cursor-pointer hover:text-blue-700 underline">
                                  Cambiar
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleGallerySlotChange(e.target.files?.[0], i)} />
                                </label>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">Hacé click en una imagen para ampliarla. La imagen principal se usa como miniatura en el catálogo.</p>
                    </div>

                    {/* ── Certificaciones ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 border-b border-gray-200 pb-1 mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
                        Certificación
                      </h4>

                      {/* Checkboxes CE y UKCA */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {[
                          { key: 'ce_logo', label: 'CE Logo' },
                          { key: 'ukca_logo', label: 'UKCA Logo' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                            <input
                              type="checkbox"
                              checked={!!formData[key]}
                              onChange={(e) => setFormData(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>

                      {/* Certificados con archivo + fechas */}
                      <div className="space-y-3">
                        {[
                          { key: 'cert_en71', label: 'Test report EN71' },
                          { key: 'cert_ftalatos', label: 'Ftalatos' },
                          { key: 'cert_tolueno', label: 'Tolueno' },
                          { key: 'cert_benceno', label: 'Benceno' },
                        ].map(({ key, label }) => {
                          const cert = formData[key];
                          return (
                            <div key={key} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-gray-700">{label}</span>
                                {cert?.file_data && (
                                  <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, [key]: null }))}
                                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                  >
                                    <X className="w-3 h-3" /> Quitar
                                  </button>
                                )}
                              </div>

                              {cert?.file_data ? (
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" /> {cert.file_name || 'Archivo cargado'}
                                  </span>
                                  {cert.file_data.startsWith('data:image') && (
                                    <a href={cert.file_data} target="_blank" rel="noreferrer" className="text-xs text-blue-500 underline">Ver</a>
                                  )}
                                </div>
                              ) : (
                                <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-800 mb-2">
                                  <Upload className="w-3.5 h-3.5" />
                                  Subir archivo (PDF / JPEG)
                                  <input
                                    type="file"
                                    accept=".pdf,image/jpeg,image/jpg,image/png"
                                    className="hidden"
                                    onChange={(e) => handleCertFileUpload(key, e.target.files?.[0])}
                                  />
                                </label>
                              )}

                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-0.5">Fecha de carga</label>
                                  <input
                                    type="date"
                                    value={cert?.upload_date || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      [key]: { ...(prev[key] || {}), upload_date: e.target.value }
                                    }))}
                                    className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-0.5">Fecha de vencimiento</label>
                                  <input
                                    type="date"
                                    value={cert?.expiry_date || ''}
                                    onChange={(e) => setFormData(prev => ({
                                      ...prev,
                                      [key]: { ...(prev[key] || {}), expiry_date: e.target.value }
                                    }))}
                                    className="w-full px-2 py-1 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </div>
                )}

                {activeSection === 'costo' && (
                  <div className="space-y-5">
                    <div className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">
                      Costo/Logística
                    </div>

                    {priceLists.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {priceLists.map((pl) => (
                          <div key={pl.id}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{pl.name}</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.listPrices?.[String(pl.id)] ?? ''}
                              onChange={(e) => setFormData((prev) => ({
                                ...prev,
                                listPrices: { ...prev.listPrices, [String(pl.id)]: e.target.value },
                              }))}
                              className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                              placeholder="0.00"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Precios por Lista ── */}
                    <ProductIndustrialSection
                      formData={formData}
                      setFormData={setFormData}
                      ports={ports}
                      priceLists={priceLists}
                      productId={editingProduct?.id || null}
                      section="prices"
                      onPortCreated={loadPorts}
                    />

                    {/* ── Moneda ── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Moneda de precio</label>
                        <div className="w-full px-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 select-none">
                          USD — Dólar
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Moneda de costo</label>
                        <select
                          value={formData.cost_currency || 'USD'}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost_currency: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        >
                          <option value="USD">USD — Dólar</option>
                          <option value="CNY">CNY — Yuan</option>
                        </select>
                      </div>
                    </div>

                    {/* ── Incoterm ── */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Incoterm</label>
                        <select
                          value={formData.incoterm || 'FOB'}
                          onChange={(e) => setFormData(prev => ({ ...prev, incoterm: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        >
                          <option value="FOB">FOB</option>
                          <option value="EXW">EXW</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tasa de cambio (CNY/USD)</label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          placeholder="Ej: 7.25"
                          value={formData.exchange_rate}
                          onChange={(e) => setFormData(prev => ({ ...prev, exchange_rate: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Margen (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.profit_margin ?? 0}
                          onChange={(e) => setFormData(prev => ({ ...prev, profit_margin: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Factor logístico</label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          placeholder="Ej: 1.15"
                          value={formData.logistics_factor}
                          onChange={(e) => setFormData(prev => ({ ...prev, logistics_factor: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* ── Costos ── */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Costo de fábrica original</label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.original_factory_cost}
                          onChange={(e) => setFormData(prev => ({ ...prev, original_factory_cost: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Costo RMB
                          {(formData.incoterm || 'FOB') === 'FOB' && (
                            <span className="ml-1 text-xs text-gray-400 font-normal">(solo EXW)</span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.cost_rmb ?? ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost_rmb: e.target.value }))}
                          disabled={(formData.incoterm || 'FOB') === 'FOB'}
                          className={`w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${(formData.incoterm || 'FOB') === 'FOB' ? 'bg-gray-100 opacity-60 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Costo FOB
                          {(formData.incoterm || 'FOB') === 'EXW' && (
                            <span className="ml-1 text-xs text-blue-500 font-normal">(calculado automático)</span>
                          )}
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.cost_fob ?? ''}
                          onChange={(e) => {
                            if ((formData.incoterm || 'FOB') !== 'EXW') {
                              setFormData(prev => ({ ...prev, cost_fob: e.target.value }));
                            }
                          }}
                          readOnly={(formData.incoterm || 'FOB') === 'EXW'}
                          className={`w-full px-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${(formData.incoterm || 'FOB') === 'EXW' ? 'bg-blue-50 border-blue-200 text-blue-800 font-semibold cursor-default' : 'bg-white border-gray-300'}`}
                        />
                      </div>
                    </div>

                    {/* ── Precios FOB ── */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio FOB{' '}
                          <span className="text-xs font-normal text-gray-400">(calculado, solo lectura)</span>
                        </label>
                        <input
                          type="number"
                          step="0.000001"
                          value={formData.price_fob ?? ''}
                          readOnly
                          className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Mejor costo con pedido</label>
                        <input
                          type="number"
                          step="0.000001"
                          min="0"
                          value={formData.best_cost_with_order}
                          onChange={(e) => setFormData(prev => ({ ...prev, best_cost_with_order: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* ── MOQ ── */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-600 border-b border-gray-200 pb-1 mb-3">Cantidades mínimas de pedido (MOQ)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">MOQ</label>
                          <input
                            type="number"
                            min="0"
                            value={formData.moq}
                            onChange={(e) => setFormData(prev => ({ ...prev, moq: e.target.value }))}
                            className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">MOQ Empaque de fábrica</label>
                          <input
                            type="number"
                            min="0"
                            value={formData.moq_factory_pack}
                            onChange={(e) => setFormData(prev => ({ ...prev, moq_factory_pack: e.target.value }))}
                            className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">MOQ OEM</label>
                          <input
                            type="number"
                            min="0"
                            value={formData.moq_oem}
                            onChange={(e) => setFormData(prev => ({ ...prev, moq_oem: e.target.value }))}
                            className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* ── Logística & TR ── */}
                    <div className="border-t border-gray-200 pt-5">
                      <div className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg mb-4">
                        Logística &amp; TR
                      </div>
                      <ProductIndustrialSection
                        formData={formData}
                        setFormData={setFormData}
                        ports={ports}
                        priceLists={priceLists}
                        productId={editingProduct?.id || null}
                        section="logistics"
                        onPortCreated={loadPorts}
                      />

                      {/* ── Master Pack ── */}
                      <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
                        <h4 className="text-sm font-semibold text-gray-700">Master Pack</h4>

                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cantidades por caja</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Piezas por caja (PCS/CTN)</label>
                              <input type="number" min="0" value={formData.pcs_per_ctn}
                                onChange={(e) => setFormData(prev => ({ ...prev, pcs_per_ctn: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Inner CTN</label>
                              <input type="number" min="0" value={formData.inner_ctn}
                                onChange={(e) => setFormData(prev => ({ ...prev, inner_ctn: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                          </div>
                        </div>

                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">EAN / DUN</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">EAN</label>
                              <input type="text" maxLength={13} value={formData.ean}
                                onChange={(e) => setFormData(prev => ({ ...prev, ean: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">DUN</label>
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={14}
                                value={formData.dun}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, '');
                                  setFormData(prev => ({ ...prev, dun: val }));
                                }}
                                className={`w-full px-4 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 ${formData.dun && formData.dun.length === 14 ? 'border-amber-400' : 'border-gray-300'}`}
                              />
                              {formData.dun && formData.dun.length === 14 && (
                                <p className="mt-1 text-xs text-amber-600">Máximo 14 dígitos alcanzado.</p>
                              )}
                            </div>
                          </div>
                        </div>

                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dimensiones master pack (cm)</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Alto</label>
                              <input type="number" step="0.01" min="0" value={formData.master_pack_height}
                                onChange={(e) => setFormData(prev => ({ ...prev, master_pack_height: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Largo</label>
                              <input type="number" step="0.01" min="0" value={formData.master_pack_length}
                                onChange={(e) => setFormData(prev => ({ ...prev, master_pack_length: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Ancho</label>
                              <input type="number" step="0.01" min="0" value={formData.master_pack_width}
                                onChange={(e) => setFormData(prev => ({ ...prev, master_pack_width: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Peso bruto (kg)</label>
                              <input type="number" step="0.01" min="0" value={formData.master_pack_gross_weight}
                                onChange={(e) => setFormData(prev => ({ ...prev, master_pack_gross_weight: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Peso neto (kg)</label>
                              <input type="number" step="0.01" min="0" value={formData.master_pack_net_weight}
                                onChange={(e) => setFormData(prev => ({ ...prev, master_pack_net_weight: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Unidades por master</label>
                              <input type="number" step="1" min="0" value={formData.units_per_master}
                                onChange={(e) => setFormData(prev => ({ ...prev, units_per_master: e.target.value }))}
                                className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === 'factory' && (
                  <div className="space-y-4">
                    <div className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">
                      Información de Fábrica
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Agente (Contacto)</label>
                      <select
                        value={formData.agent_id || ''}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setFormData(prev => ({ ...prev, agent_id: selectedId }));
                        }}
                        className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                      >
                        <option value="">Seleccionar agente</option>
                        {factoryContacts.map(fc => (
                          <option key={fc.id} value={fc.id}>{fc.name}</option>
                        ))}
                      </select>
                    </div>

                    {(() => {
                      const selected = factoryContacts.find(fc => String(fc.id) === String(formData.agent_id));
                      if (!selected) return null;
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                          <h4 className="text-sm font-semibold text-gray-700">Datos del agente seleccionado</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-500">Nombre:</span>
                              <span className="ml-2 text-gray-800 font-medium">{selected.name}</span>
                            </div>
                            {selected.email && (
                              <div>
                                <span className="text-gray-500">Email:</span>
                                <span className="ml-2 text-gray-800">{selected.email}</span>
                              </div>
                            )}
                            {selected.phone && (
                              <div>
                                <span className="text-gray-500">Teléfono:</span>
                                <span className="ml-2 text-gray-800">{selected.phone}</span>
                              </div>
                            )}
                            {(selected.city || selected.country) && (
                              <div>
                                <span className="text-gray-500">Ubicación:</span>
                                <span className="ml-2 text-gray-800">{[selected.city, selected.country].filter(Boolean).join(', ')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                )}

                {activeSection === 'multimedia' && (
                  <div className="space-y-4">
                    <div className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg">
                      Multimedia - Diseño &amp; Producto
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Galería de imágenes</label>
                      <div className="flex items-center gap-3">
                        <label className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-200 transition-colors flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Subir imágenes
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleGalleryChange(e.target.files)}
                          />
                        </label>
                      </div>
                      {Array.isArray(formData.gallery_images) && formData.gallery_images.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                          {formData.gallery_images.map((img, index) => (
                            <div key={`${img}-${index}`} className="relative border border-gray-200 rounded-lg overflow-hidden">
                              <img src={img} alt="Galería" className="w-full h-24 object-cover" />
                              <div className="absolute top-1 right-1 flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => moveGalleryItem(index, -1)}
                                  className="p-1 bg-white rounded hover:bg-gray-100"
                                >
                                  <ChevronUp className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => moveGalleryItem(index, 1)}
                                  className="p-1 bg-white rounded hover:bg-gray-100"
                                >
                                  <ChevronDown className="w-3 h-3" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    gallery_images: prev.gallery_images.filter((_, i) => i !== index)
                                  }))}
                                  className="p-1 bg-white rounded hover:bg-gray-100"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video de producto (URL o archivo)</label>
                      <div className="flex flex-col md:flex-row gap-3">
                        <input
                          type="text"
                          placeholder="https://..."
                          value={formData.video_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                          className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                        />
                        <label className="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium cursor-pointer hover:bg-gray-200 transition-colors flex items-center gap-2">
                          <Video className="w-4 h-4" />
                          Subir video
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                          />
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">URL pública del producto</label>
                      <div className="flex flex-col md:flex-row gap-3">
                        <input
                          type="text"
                          value={publicUrl}
                          readOnly
                          placeholder="Generar URL pública"
                          className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const base = typeof window !== 'undefined' ? window.location.origin : '';
                            const slug = formData.product_code || editingProduct?.id;
                            if (!slug) {
                              alert('Para generar la URL pública, primero guardá el producto o ingresá un código.');
                              return;
                            }
                            const url = `${base}/products/public/${slug}`;
                            setPublicUrl(url);
                            if (navigator.clipboard) {
                              navigator.clipboard.writeText(url).catch(() => {});
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2"
                        >
                          <Link className="w-4 h-4" />
                          Generar URL pública
                        </button>
                        {publicUrl && (
                          <button
                            type="button"
                            onClick={() => navigator.clipboard?.writeText(publicUrl)}
                            className="px-4 py-2 bg-gray-100 rounded-lg font-medium flex items-center gap-2"
                          >
                            <Clipboard className="w-4 h-4" />
                            Copiar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ── Diseño & Producto ── */}
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-semibold text-gray-600 mb-3">Diseño &amp; Producto</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Mejoras del producto</label>
                          <textarea
                            value={formData.product_improvements}
                            onChange={(e) => setFormData(prev => ({ ...prev, product_improvements: e.target.value }))}
                            rows={3}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Descripción para ecommerce</label>
                          <textarea
                            value={formData.ecommerce_description}
                            onChange={(e) => setFormData(prev => ({ ...prev, ecommerce_description: e.target.value }))}
                            rows={4}
                            className="w-full px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}


                <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium flex items-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    {editingProduct ? 'Actualizar' : 'Crear'} Producto
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestión de categorías */}
      <AnimatePresence>
        {showCategoryManager && (
          <CategoryManagerModal
            categories={categories}
            editingCategory={editingCategory}
            categoryFormData={categoryFormData}
            setCategoryFormData={setCategoryFormData}
            onClose={() => {
              setShowCategoryManager(false);
              resetCategoryForm();
            }}
            onSave={async (categoryData) => {
              try {
                const payload = {
                  ...categoryData,
                  parent: categoryData.parent ? parseInt(categoryData.parent) : null,
                };
                if (editingCategory) {
                  await categoryService.update(editingCategory.id, payload);
                } else {
                  await categoryService.create(payload);
                }
                await loadCategories();
                resetCategoryForm();
                // No cerramos el modal: el usuario puede seguir creando/editando
              } catch (error) {
                console.error('Error guardando categoría:', error);
                alert(`No se pudo guardar la categoría.\n${parseErrorMessage(error)}`);
              }
            }}
            onDelete={async (categoryId) => {
              if (window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
                try {
                  await categoryService.delete(categoryId);
                  await loadCategories();
                } catch (error) {
                  console.error('Error eliminando categoría:', error);
                  alert(`No se pudo eliminar la categoría.\n${parseErrorMessage(error)}`);
                }
              }
            }}
            onEdit={(category) => {
              setEditingCategory(category);
              setCategoryFormData({
                name: category.name || '',
                description: category.description || '',
                parent: category.parent ? String(category.parent) : null,
                is_active: category.is_active !== false
              });
            }}
          />
        )}
      </AnimatePresence>


      {/* Lightbox de imagen ampliada */}
      <AnimatePresence>
        {imagePreview && (
          <motion.div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setImagePreview('')}
          >
            <motion.div
              className="relative max-w-5xl w-full max-h-[92vh] flex items-center justify-center"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Botón cerrar */}
              <button
                onClick={() => setImagePreview('')}
                className="absolute top-3 right-3 z-10 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full transition-colors shadow-lg"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={imagePreview}
                alt="Producto ampliado"
                className="max-h-[88vh] max-w-full w-auto object-contain rounded-2xl shadow-2xl"
                style={{ border: '2px solid rgba(255,255,255,0.15)' }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

// Modal de importación masiva de stock
const StockImportModal = ({ 
  products, 
  onClose, 
  onImport 
}) => {
  const [csvData, setCsvData] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [adjustments, setAdjustments] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      setCsvData(text);
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return;
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    // Buscar índices de columnas
    const idIndex = headers.findIndex(h => h.includes('id') || h.includes('producto'));
    const stockIndex = headers.findIndex(h => h.includes('stock') || h.includes('cantidad'));
    
    if (idIndex === -1 || stockIndex === -1) {
      alert('El archivo no tiene el formato correcto. Debe tener columnas "ID" (o "Producto") y "Stock" (o "Cantidad").');
      return;
    }

    const parsed = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const productId = values[idIndex];
      const stock = values[stockIndex];
      
      if (productId && stock) {
        // Buscar producto por ID o nombre
        const product = products.find(p => 
          p.id === productId || 
          p.id.toString() === productId ||
          p.name.toLowerCase() === productId.toLowerCase()
        );
        
        if (product) {
          parsed.push({
            id: parseInt(product.id),
            stock: parseFloat(stock) || 0,
            name: product.name,
            currentStock: product.stock || 0
          });
        }
      }
    }
    
    setParsedData(parsed);
  };

  const handleManualInput = () => {
    // Permitir entrada manual de datos
    const manualData = csvData.split('\n')
      .filter(line => line.trim())
      .map(line => {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          const productId = parts[0];
          const stock = parseFloat(parts[1]) || 0;
          const product = products.find(p => 
            !p.is_service && ( // Solo productos, no servicios
              p.id === productId || 
              p.id.toString() === productId ||
              p.name.toLowerCase() === productId.toLowerCase()
            )
          );
          
          if (product) {
            return {
              id: parseInt(product.id),
              stock: stock,
              name: product.name,
              currentStock: product.stock || 0
            };
          }
        }
        return null;
      })
      .filter(Boolean);
    
    setParsedData(manualData);
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      alert('No hay datos para importar. Verificá que el archivo tenga filas con datos.');
      return;
    }

    setIsProcessing(true);
    try {
      const updates = parsedData.map(item => ({
        id: item.id,
        stock: item.stock
      }));
      
      const results = await onImport(updates);
      setImportResults(results);
      // Recargar historial después de importar
      if (showHistory) {
        loadAdjustmentsHistory();
      }
    } catch (error) {
      console.error('Error importando stock:', error);
      alert(`No se pudo importar el stock.\n${parseErrorMessage(error, 'Verificá el formato del archivo e intentá de nuevo.')}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const loadAdjustmentsHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await productService.getStockAdjustments({ limit: 100 });
      setAdjustments(data || []);
    } catch (error) {
      console.error('Error cargando historial de ajustes:', error);
      setAdjustments([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (showHistory) {
      loadAdjustmentsHistory();
    }
  }, [showHistory]);

  const handleExport = () => {
    // Crear CSV con productos y stock actual
    const headers = ['ID', 'Producto', 'Stock Actual'];
    const rows = products.map(p => [
      p.id,
      p.name,
      p.stock || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `stock_actual_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      className="bg-white border border-gray-200 rounded-xl shadow-sm p-8"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
    >
      <motion.div
        className="w-full max-h-[88vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Importar/Exportar Stock
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs para Importar/Exportar y Historial */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setShowHistory(false)}
            className={`px-4 py-2 font-medium transition-colors ${
              !showHistory
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Importar/Exportar
          </button>
          <button
            onClick={() => setShowHistory(true)}
            className={`px-4 py-2 font-medium transition-colors ${
              showHistory
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Historial de Ajustes
          </button>
        </div>

        {showHistory ? (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Importante:</strong> Los ajustes de inventario deben realizarse todos los primeros días hábiles de cada mes. 
                Los registros fuera de fecha se muestran en color rojo.
              </p>
            </div>

            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : adjustments.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No hay ajustes registrados</p>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left">Fecha/Hora</th>
                      <th className="px-4 py-2 text-left">Producto</th>
                      <th className="px-4 py-2 text-right">Stock Anterior</th>
                      <th className="px-4 py-2 text-right">Stock Nuevo</th>
                      <th className="px-4 py-2 text-right">Diferencia</th>
                      <th className="px-4 py-2 text-left">Usuario</th>
                      <th className="px-4 py-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustments.map((adj) => {
                      const adjDate = new Date(adj.adjustment_date);
                      const isLate = !adj.is_on_time;
                      return (
                        <tr
                          key={adj.id}
                          className={`border-t border-gray-200 ${
                            isLate ? 'bg-red-50' : ''
                          }`}
                        >
                          <td className="px-4 py-2">
                            {adjDate.toLocaleString('es-AR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="px-4 py-2 font-medium">{adj.product_name}</td>
                          <td className="px-4 py-2 text-right">{adj.old_stock}</td>
                          <td className="px-4 py-2 text-right font-semibold">{adj.new_stock}</td>
                          <td className={`px-4 py-2 text-right font-semibold ${
                            adj.difference > 0 ? 'text-green-600' : 
                            adj.difference < 0 ? 'text-red-600' : 
                            'text-gray-600'
                          }`}>
                            {adj.difference > 0 ? '+' : ''}{adj.difference}
                          </td>
                          <td className="px-4 py-2">{adj.adjusted_by_name || 'N/A'}</td>
                          <td className="px-4 py-2 text-center">
                            {isLate ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Fuera de fecha
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                A tiempo
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        ) : !importResults ? (
          <div className="space-y-6">
            {/* Botón de exportar */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Exportar Stock Actual</h3>
                  <p className="text-sm text-blue-700">Descarga un archivo CSV con el stock actual de todos los productos</p>
                </div>
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar CSV
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Importar Stock</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subir archivo CSV
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <FileSpreadsheet className="w-12 h-12 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Haz clic para seleccionar un archivo CSV
                    </span>
                    <span className="text-xs text-gray-500">
                      Formato: ID,Stock o Producto,Stock
                    </span>
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  O ingresar datos manualmente (ID,Stock por línea)
                </label>
                <textarea
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  onBlur={handleManualInput}
                  placeholder="1,100&#10;2,50&#10;3,200"
                  rows={8}
                  className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 font-mono text-sm"
                />
              </div>

              {parsedData.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">
                    Vista Previa ({parsedData.length} productos)
                  </h3>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left">ID</th>
                          <th className="px-4 py-2 text-left">Producto</th>
                          <th className="px-4 py-2 text-right">Stock Actual</th>
                          <th className="px-4 py-2 text-right">Stock Nuevo</th>
                          <th className="px-4 py-2 text-right">Diferencia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedData.map((item, index) => {
                          const difference = item.stock - item.currentStock;
                          return (
                            <tr key={index} className="border-t border-gray-200">
                              <td className="px-4 py-2">{item.id}</td>
                              <td className="px-4 py-2">{item.name}</td>
                              <td className="px-4 py-2 text-right">{item.currentStock}</td>
                              <td className="px-4 py-2 text-right font-semibold">{item.stock}</td>
                              <td className={`px-4 py-2 text-right font-semibold ${
                                difference > 0 ? 'text-green-600' : 
                                difference < 0 ? 'text-red-600' : 
                                'text-gray-600'
                              }`}>
                                {difference > 0 ? '+' : ''}{difference}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleImport}
                  disabled={parsedData.length === 0 || isProcessing}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Procesar Importación
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-800 mb-4">
                Importación Completada
              </h3>
              <div className="space-y-2 text-sm">
                <p><strong>Productos actualizados:</strong> {importResults.updated}</p>
                {importResults.not_found?.length > 0 && (
                  <p className="text-red-600">
                    <strong>No encontrados:</strong> {importResults.not_found.length}
                  </p>
                )}
                {importResults.errors?.length > 0 && (
                  <p className="text-red-600">
                    <strong>Errores:</strong> {importResults.errors.length}
                  </p>
                )}
              </div>
            </div>

            {importResults.differences && importResults.differences.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  Diferencias de Stock
                </h3>
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-xl">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left">ID</th>
                        <th className="px-4 py-2 text-left">Producto</th>
                        <th className="px-4 py-2 text-right">Stock Anterior</th>
                        <th className="px-4 py-2 text-right">Stock Nuevo</th>
                        <th className="px-4 py-2 text-right">Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.differences.map((diff, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-4 py-2">{diff.product_id}</td>
                          <td className="px-4 py-2">{diff.product_name}</td>
                          <td className="px-4 py-2 text-right">{diff.old_stock}</td>
                          <td className="px-4 py-2 text-right font-semibold">{diff.new_stock}</td>
                          <td className={`px-4 py-2 text-right font-semibold ${
                            diff.difference > 0 ? 'text-green-600' : 
                            diff.difference < 0 ? 'text-red-600' : 
                            'text-gray-600'
                          }`}>
                            {diff.difference > 0 ? '+' : ''}{diff.difference}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setImportResults(null);
                  setParsedData([]);
                  setCsvData('');
                  onClose();
                }}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Modal de gestión de categorías
const CategoryManagerModal = ({ 
  categories, 
  editingCategory, 
  categoryFormData, 
  setCategoryFormData,
  onClose, 
  onSave, 
  onDelete, 
  onEdit 
}) => {
  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = categoryFormData.name?.trim();
    if (!trimmedName) {
      alert('El nombre de la categoría es obligatorio.');
      return;
    }
    const duplicate = categories.find(
      (c) =>
        c.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
        (!editingCategory || c.id !== editingCategory.id)
    );
    if (duplicate) {
      alert(`Ya existe una categoría con el nombre "${trimmedName}". Elegí un nombre diferente.`);
      return;
    }
    await onSave(categoryFormData);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 20 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-xl font-bold text-gray-800">
            {editingCategory ? 'Editar Categoría' : 'Gestionar Categorías'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Formulario de categoría */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              {editingCategory ? 'Editar' : 'Nueva'} Categoría
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={categoryFormData.description || ''}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría padre
                </label>
                <select
                  value={categoryFormData.parent || ''}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, parent: e.target.value || null }))}
                  className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                >
                  <option value="">Sin padre (raíz)</option>
                  {categories
                    .filter((c) => (c.level || 0) < 4 && (!editingCategory || c.id !== editingCategory.id))
                    .map((cat) => (
                      <option key={cat.id} value={cat.id}>{'─'.repeat(cat.level || 0)} {cat.full_path || cat.name}</option>
                    ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={categoryFormData.is_active}
                  onChange={(e) => setCategoryFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Activa
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                {editingCategory && (
                  <button
                    type="button"
                    onClick={() => {
                      onDelete(editingCategory.id);
                    }}
                    className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setCategoryFormData({ name: '', description: '', parent: null, is_active: true });
                    onEdit(null);
                  }}
                  className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Limpiar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingCategory ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>

          {/* Lista de categorías */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              Categorías Existentes ({categories.length})
            </h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">
                  No hay categorías creadas
                </p>
              ) : (
                categories.map((category) => (
                  <motion.div
                    key={category.id}
                    className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${
                      editingCategory?.id === category.id
                        ? 'border-blue-500 bg-blue-50'
                        : category.is_active
                        ? 'border-gray-200 bg-white hover:border-gray-300'
                        : 'border-gray-100 bg-gray-50 opacity-60'
                    }`}
                    onClick={() => onEdit(category)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Tag className={`w-4 h-4 ${category.is_active ? 'text-blue-500' : 'text-gray-400'}`} />
                          <span className="font-semibold text-gray-800" style={{ paddingLeft: `${(category.level || 0) * 12}px` }}>
                            {category.full_path || category.name}
                          </span>
                          {(category.level || 0) > 0 && (
                            <span className="text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">N{category.level + 1}</span>
                          )}
                          {!category.is_active && (
                            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">Inactiva</span>
                          )}
                        </div>
                        {category.description && (
                          <p className="text-xs text-gray-600 mt-1">{category.description}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(category.id);
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Modal de Importación de Productos desde Excel ──
const ProductImportModal = ({ onClose, onImport, onExport }) => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setResults(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResults(null);
    }
  };

  const handleProcess = async () => {
    if (!file) return;
    setIsProcessing(true);
    try {
      const res = await onImport(file);
      setResults(res);
    } catch (error) {
      setResults({ total: 0, created: 0, updated: 0, errors: [error.message || 'Error desconocido'] });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      className="bg-white border border-gray-200 rounded-xl shadow-sm p-8"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
    >
      <motion.div
        className="w-full max-h-[88vh] overflow-y-auto"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Importar / Exportar Productos</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Sección Exportar */}
        <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-2 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exportar productos
          </h3>
          <p className="text-sm text-green-700 mb-3">
            Descarga todos los productos en formato Excel. Puedes usar este archivo como plantilla para importar nuevos productos o actualizar existentes.
          </p>
          <button
            onClick={() => {
              onExport();
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Descargar Excel
          </button>
        </div>

        {/* Sección Importar */}
        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
          <h3 className="text-lg font-semibold text-amber-800 mb-2 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Importar Productos
          </h3>
          <div className="text-sm text-amber-700 mb-4 space-y-1">
            <p><strong>Instrucciones:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Usa el archivo exportado como plantilla.</li>
              <li>Si la columna <strong>ID</strong> tiene valor, se <strong>actualiza</strong> el producto existente.</li>
              <li>Si la columna <strong>ID</strong> está vacía, se <strong>crea</strong> un producto nuevo.</li>
              <li>La columna <strong>Nombre</strong> es obligatoria.</li>
              <li>Para catálogos (Marca, Categoría, etc.) usar el nombre exacto.</li>
            </ul>
          </div>

          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
              dragActive ? 'border-amber-500 bg-amber-100' : 'border-gray-300 hover:border-amber-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              id="product-import-file"
            />
            {file ? (
              <div className="space-y-2">
                <FileSpreadsheet className="w-10 h-10 text-amber-600 mx-auto" />
                <p className="font-medium text-gray-800">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                <button
                  onClick={() => { setFile(null); setResults(null); }}
                  className="text-sm text-red-500 hover:text-red-700 underline"
                >
                  Cambiar archivo
                </button>
              </div>
            ) : (
              <label htmlFor="product-import-file" className="cursor-pointer space-y-2 block">
                <Upload className="w-10 h-10 text-gray-400 mx-auto" />
                <p className="text-gray-600">Arrastra un archivo Excel aquí o <span className="text-amber-600 font-semibold">haz clic para seleccionar</span></p>
                <p className="text-xs text-gray-400">Formatos: .xlsx, .xls</p>
              </label>
            )}
          </div>

          {/* Botón Procesar */}
          {file && !results && (
            <button
              onClick={handleProcess}
              disabled={isProcessing}
              className={`mt-4 w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 transition-colors ${
                isProcessing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Procesando...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Procesar Importación
                </>
              )}
            </button>
          )}

          {/* Resultados */}
          {results && (
            <div className="mt-4 p-4 rounded-lg bg-white border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Resultados de la Importación
              </h4>
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="text-center p-2 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{results.total}</p>
                  <p className="text-xs text-blue-600">Total filas</p>
                </div>
                <div className="text-center p-2 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{results.created}</p>
                  <p className="text-xs text-green-600">Creados</p>
                </div>
                <div className="text-center p-2 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-700">{results.updated}</p>
                  <p className="text-xs text-amber-600">Actualizados</p>
                </div>
                <div className="text-center p-2 bg-orange-50 rounded-lg">
                  <p className="text-2xl font-bold text-orange-700">{(results.warnings || []).length}</p>
                  <p className="text-xs text-orange-600">Con avisos</p>
                </div>
              </div>

              {/* Advertencias por producto */}
              {results.warnings && results.warnings.length > 0 && (
                <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                  <p className="text-sm font-semibold text-orange-700 flex items-center gap-1 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Campos no resueltos ({results.warnings.length} {results.warnings.length === 1 ? 'producto' : 'productos'})
                  </p>
                  <p className="text-xs text-orange-600 mb-2">
                    Los productos fueron {results.warnings[0]?.action === 'actualizado' ? 'procesados' : 'guardados'} correctamente, pero los siguientes campos no pudieron vincularse por no coincidir con un valor existente en el sistema. Podés corregirlos manualmente desde el producto o agregar los valores al catálogo correspondiente.
                  </p>
                  <div className="max-h-52 overflow-y-auto space-y-2">
                    {results.warnings.map((w, idx) => (
                      <div key={idx} className="bg-white rounded-lg border border-orange-100 p-2">
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Fila {w.row} — <span className="text-gray-900">{w.name}</span>
                          <span className="ml-1 text-orange-500">({w.action})</span>
                        </p>
                        <ul className="space-y-0.5">
                          {w.warnings.map((warn, wIdx) => (
                            <li key={wIdx} className="text-xs text-orange-700 flex items-start gap-1">
                              <span className="mt-0.5 shrink-0">•</span>
                              <span>{warn}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errores críticos */}
              {results.errors && results.errors.length > 0 && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-1 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Errores — filas no procesadas ({results.errors.length})
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {results.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  onClose();
                  window.location.reload();
                }}
                className="mt-4 w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                Cerrar y Recargar
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProductManager;

